import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { fmtDate, fmtDateTime } from "@/lib/dateFormat";
import { istToday, istDatePlus } from "@/lib/istDate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, ArrowRight, Check, Trash2, Plus, AlertCircle, ShieldAlert, History, CreditCard, Upload, ChevronDown } from "lucide-react";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useConfig, ALL_CONFIG_GROUPS } from "@/hooks/useConfig";

const STEPS = ["Order Details", "SKU Selection", "Pricing Summary", "Payment Terms", "Logistics", "Review & Submit"];
// FINANCE BATCH 1 (1.4c): four payment status words only
const PAY_STATUS_COLORS = { "Unpaid": "bg-zinc-100 text-zinc-700", "Partially Paid": "bg-amber-100 text-amber-800", "Fully Paid": "bg-green-100 text-green-800", "Short Paid": "bg-orange-100 text-orange-800" };
const ORDER_STATUS_COLORS = { "Draft": "bg-zinc-200 text-zinc-700", "Pending with Manager": "bg-purple-100 text-purple-800", "Pending with Admin": "bg-amber-100 text-amber-800", "Pending with Finance": "bg-blue-100 text-blue-800", "At Warehouse": "bg-orange-100 text-orange-800", "Shipped": "bg-teal-100 text-teal-800", "Delivered": "bg-green-100 text-green-800", "Order Closed": "bg-green-200 text-green-900", "Rejected": "bg-red-100 text-red-800" };

class CreateOrderErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("CreateOrderPage crash:", error, info); }
  render() {
    if (this.state.hasError) {
      return <div className="p-8 text-center"><h2 className="text-lg font-bold text-red-600 mb-2">Order creation page encountered an error</h2><p className="text-sm text-zinc-600 mb-4">{this.state.error?.message || "Unknown error"}</p><pre className="text-xs text-left bg-zinc-100 p-3 rounded max-h-40 overflow-auto mb-4">{this.state.error?.stack}</pre><button onClick={() => window.location.reload()} className="px-4 py-2 bg-blue-600 text-white rounded text-sm">Reload Page</button></div>;
    }
    return this.props.children;
  }
}

function CreateOrderPageInner() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("Admin") || (user?.roles || []).includes("admin");
  const { getValues } = useConfig(ALL_CONFIG_GROUPS);
  const PAYMENT_MODES = getValues("payment_modes");
  const ORDER_TYPES = getValues("order_types");
  const [step, setStep] = useState(0);
  const [channels, setChannels] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [customerSearchText, setCustomerSearchText] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [channelSKUs, setChannelSKUs] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [brandBlockMsg, setBrandBlockMsg] = useState("");   // FX1 B5
  const [serverDate, setServerDate] = useState(istToday());
  const [fieldErrors, setFieldErrors] = useState({});
  const [proofFiles, setProofFiles] = useState([]);     // FOLLOW-UP 3 (12a) — payment screenshots
  const [proofInputKey, setProofInputKey] = useState(0);

  // Customer history
  const [customerHistory, setCustomerHistory] = useState({ orders: [], total: 0 });
  const [historyOrder, setHistoryOrder] = useState(null);
  const [creditCheck, setCreditCheck] = useState(null);

  // Form state
  const [order, setOrder] = useState({
    channel_id: "", channel_name: "", customer_id: "", customer_name: "",
    order_date: "", order_type: "B2B", customer_state: "", customer_max_discount: 100,
    po_number: "", po_date: "", pi_reference: "", priority: "standard",
    warehouse_id: "", warehouse_name: "",
    fulfilment_type: "warehouse_dispatch", ship_from_location: "", ship_from_transporter: "",
    step_notes: { "Order Details": "", "SKU Selection": "", "Pricing Summary": "", "Payment Terms": "", "Logistics": "", "Review & Submit": "" },
    delivery_address: null, delivery_address_index: 0,
  });
  const [lineItems, setLineItems] = useState([]);

  // Pre-fill from URL params (PO Inbox "Create Order" flow)
  const [suggestedItems, setSuggestedItems] = useState([]);
  useEffect(() => {
    const prefillPO = searchParams.get("po_number");
    const prefillDate = searchParams.get("po_date");
    if (prefillPO || prefillDate) {
      setOrder(prev => ({ ...prev, po_number: prefillPO || prev.po_number, po_date: prefillDate || prev.po_date }));
    }
    // Parse suggested items from PO Inbox
    const sugParam = searchParams.get("suggested");
    if (sugParam) {
      try {
        const sug = JSON.parse(decodeURIComponent(sugParam));
        if (sug.items && sug.items.length > 0) {
          setSuggestedItems(sug.items);
          if (sug.appointment_date) setOrder(prev => ({ ...prev, appointment_date: sug.appointment_date }));
        }
      } catch(e) { console.error("Failed to parse suggested items:", e); }
    }
  }, []);

  const [logistics, setLogistics] = useState({ warehouse_id: "", warehouse_name: "", dispatch_date: "", courier_partner: "", special_instructions: "" });

  // Payment state
  const [paymentType, setPaymentType] = useState("");
  const [paymentForm, setPaymentForm] = useState({
    amount_paid: 0, payment_date: "", payment_mode: "", payment_reference: "", payment_notes: "",
    expected_payment_date: "",
  });

  // PO duplicate check state
  const [poCheck, setPoCheck] = useState(null); // { exists, same_customer, existing_order }
  const [poCheckTimer, setPoCheckTimer] = useState(null);

  // Comments/notes tracked with author + timestamp
  const [reviewNotes, setReviewNotes] = useState("");

  // Discount request

  const fmt = (n) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.round((n || 0) * 100) / 100);
  const r2 = (n) => Math.round((n || 0) * 100) / 100; // Display rounding only — NOT business logic

  // Server-calculated totals (Rule 11: all business logic on backend)
  const [calcResult, setCalcResult] = useState({ items: [], totals: { net_value: 0, gst_amount: 0, cgst: 0, sgst: 0, igst: 0, total: 0, total_discount: 0 } });

  const recalculate = async (items) => {
    if (!items || !items.some(i => i.sku_id)) {
      setCalcResult({ items: [], totals: { net_value: 0, gst_amount: 0, cgst: 0, sgst: 0, igst: 0, total: 0, total_discount: 0 } });
      return;
    }
    if (order.order_type === "Sample FOC") {
      setCalcResult({ items: items.map(() => ({ net_value: 0, gst_amount: 0, cgst: 0, sgst: 0, igst: 0, total: 0, discount_amount: 0 })), totals: { net_value: 0, gst_amount: 0, cgst: 0, sgst: 0, igst: 0, total: 0, total_discount: 0 } });
      return;
    }
    try {
      const { data } = await api.post("/calculate/order-totals", {
        line_items: items.filter(i => i.sku_id).map(i => ({ selling_price: i.selling_price || i.mrp, quantity: i.quantity || 1, discount_percent: i.discount_percent || 0, gst_percent: i.gst_percent || 0, gst_treatment: i.gst_treatment || "Exclusive" })),
        customer_state: order.customer_state || "",
      });
      setCalcResult({ items: data.line_items || [], totals: data.totals || {}, company_state: data.company_state || "", is_inter_state: !!data.is_inter_state });
    } catch (err) { console.error("API post /calculate/order-totals error:", err); }
  };

  useEffect(() => { const t = setTimeout(() => recalculate(lineItems), 300); return () => clearTimeout(t); }, [lineItems, order.customer_state, order.order_type]);

  // FX1 B5 — tell the user about a brand "do not sell" rule while they pick SKUs,
  // not only after they reach the last step. Read-only check, writes nothing.
  useEffect(() => {
    const picked = lineItems.filter(i => i.sku_id).map(i => ({ sku_id: i.sku_id, sku_name: i.sku_name }));
    if (picked.length === 0 || !order.customer_id) { setBrandBlockMsg(""); return; }
    let alive = true;
    api.post("/orders/brand-check", {
      customer_id: order.customer_id,
      customer_state: order.customer_state,
      delivery_address: order.delivery_address,
      line_items: picked,
    }).then(({ data }) => { if (alive) setBrandBlockMsg((data.messages || []).join("; ")); })
      .catch(() => { if (alive) setBrandBlockMsg(""); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(lineItems.map(i => i.sku_id)), order.customer_id, order.customer_state,
      JSON.stringify(order.delivery_address)]);

  // Read server-calculated values for display (no local computation)
  const getCalc = (idx) => (calcResult.items || [])[idx] || { net_value: 0, gst_amount: 0, cgst: 0, sgst: 0, igst: 0, total: 0, discount_amount: 0 };
  // PASS 2 (B1): the company's own state is never hardcoded in the browser — the server answers
  // with it on every /calculate/order-totals call (configuration company_info / company_state).

  // Get server date on mount
  useEffect(() => {
    const init = async () => {
      try {
        const [chRes, whRes, dateRes, meRes] = await Promise.all([
          api.get("/channels"), api.get("/warehouses"), api.get("/server-date"), api.get("/auth/me"),
        ]);
        // Fix 2: Filter channels by user's assigned_channels (Sales Exec restriction)
        // Admin sees ALL channels — no filtering
        const me = meRes.data;
        const meRoles = me.roles || [];
        const meIsAdmin = meRoles.includes("Admin") || meRoles.includes("admin");
        const assignedCh = me.assigned_channels || [];
        const allChannels = chRes.data || [];
        const filteredChannels = meIsAdmin ? allChannels : (assignedCh.length > 0 ? allChannels.filter(ch => assignedCh.includes(ch._id)) : allChannels);
        setChannels(filteredChannels);
        setWarehouses(whRes.data);
        const sd = dateRes.data.date;
        setServerDate(sd);
        setOrder(prev => ({ ...prev, order_date: sd }));
        setPaymentForm(prev => ({ ...prev, payment_date: sd }));
      } catch (err) { console.error("CreateOrderPage error:", err); }
    };
    init();
  }, []);

  // Load customers when channel changes
  const onChannelChange = useCallback(async (channelId) => {
    const ch = channels.find(c => c._id === channelId);
    setOrder(prev => ({ ...prev, channel_id: channelId, channel_name: ch?.name || "", customer_id: "", customer_name: "", customer_state: "", customer_max_discount: 100 }));
    setLineItems([]);
    setCustomerHistory({ orders: [], total: 0 });
    setCreditCheck(null);
    if (channelId) {
      try {
        const { data } = await api.get("/customers", { params: { channel_id: channelId } });
        // PASS 2 ZIP 1 FIX-UP (Fix 3b): the picker never offers an Inactive customer
        setFilteredCustomers((data || []).filter(c => (c.status || "") !== "Inactive"));
      } catch { setFilteredCustomers([]); }
    } else { setFilteredCustomers([]); }
  }, [channels]);

  const [creditBlock, setCreditBlock] = useState(null);
  // FOLLOW-UP 4 (8e): an overdue still inside the grace only warns — the rep has to tick it
  const [softOverdue, setSoftOverdue] = useState([]);
  const [softAck, setSoftAck] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [showOverride, setShowOverride] = useState(false);
  const [availableCNs, setAvailableCNs] = useState(null);
  const [appliedCNs, setAppliedCNs] = useState([]);
  const [partialCNId, setPartialCNId] = useState(null);
  const [partialAmt, setPartialAmt] = useState("");
  const [showCNWarning, setShowCNWarning] = useState(null);
  const totalCNApplied = appliedCNs.reduce((s, cn) => s + (cn.amount_applied || 0), 0);

  // FX3b item 5 — Direct Partner order rules on the entry screen
  const [dpInfo, setDpInfo] = useState(null);
  const [dpReason, setDpReason] = useState("");

  // Load customer history + credit check when customer changes
  const onCustomerChange = useCallback(async (customerId) => {
    const cust = filteredCustomers.find(c => c._id === customerId);
    const billingAddr = { ...cust?.billing_address, label: "Billing Address (default)" };
    const custDeliveryAddrs = cust?.delivery_addresses || [];
    const deliveryAddrs = [billingAddr, ...custDeliveryAddrs.map((da, i) => ({ ...da, label: da.label || `Delivery ${i + 1}` }))];
    const defaultAddr = deliveryAddrs[0] || billingAddr;

    setOrder(prev => ({
      ...prev, customer_id: customerId, customer_name: cust?.company_name || cust?.name || "",
      customer_state: cust?.state || defaultAddr?.state || "",
      customer_max_discount: cust?.max_discount ?? 100,
      delivery_address: defaultAddr,
      delivery_address_index: 0,
      _delivery_options: deliveryAddrs,
    }));
    setCreditBlock(null);
    setSoftOverdue([]); setSoftAck(false);
    if (customerId) {
      try {
        const { data } = await api.get(`/orders/customer-history/${customerId}`);
        setCustomerHistory(data);
      } catch { setCustomerHistory({ orders: [], total: 0 }); }
      // Credit check for hard block
      try {
        const { data: cc } = await api.get(`/customers/${customerId}/credit-check`);
        setCreditCheck(cc);
        if (cc.blocked) setCreditBlock(cc);
        const _soft = (cc.warnings || []).filter(w => w.type === "overdue_soft")
          .flatMap(w => w.orders || []);
        setSoftOverdue(_soft);
        setSoftAck(false);
      } catch { setCreditCheck(null); }
      // Fetch available CNs for this customer
      try {
        const { data: cnData } = await api.get(`/credit-notes/customer/${customerId}/summary`);
        setAvailableCNs(cnData);
      } catch (err) {
        console.error("Failed to load Credit Notes:", err);
        setAvailableCNs(null);
      }
    }
  }, [filteredCustomers]);

  // Load SKUs when entering step 2
  useEffect(() => {
    if (step === 1 && order.channel_id) {
      api.get("/catalogue/skus", { params: { channel_id: order.channel_id } }).then(r => setChannelSKUs((r.data || []).filter(s => s.status !== "Draft"))).catch(err => console.error("API get /catalogue/skus error:", err));
      api.get("/rto-stock?status=Available").then(r => setRtoStockItems(r.data || [])).catch(() => setRtoStockItems([]));
    }
  }, [step, order.channel_id]);

  // Credit check when payment type is credit_sale
  useEffect(() => {
    if (paymentType === "credit_sale" && order.customer_id && totals.total > 0) {
      api.get(`/orders/credit-check/${order.customer_id}`, { params: { new_order_value: totals.total } })
        .then(r => setCreditCheck(r.data)).catch(err => console.error("CreateOrderPage error:", err));
    }
  }, [paymentType, order.customer_id]);



  // ─── SKU LINE ITEM LOGIC ────────────────────────────────────

  const getSKUChannelData = (sku) => (sku.channel_access || []).find(c => c.channel_id === order.channel_id && c.enabled) || null;

  const addLineItem = () => {
    setLineItems([...lineItems, { sku_id: "", sku_name: "", quantity: 1, selling_price: 0, gst_percent: 0, gst_treatment: "Exclusive", mrp: 0, max_discount_sku: 0, effective_max_discount: 0, discount_percent: 0, discount_exceeded: false, moq: 1, moq_error: false, upmc: 0, carton_error: false }]);
  };

  const applySuggestedItems = () => {
    if (!suggestedItems.length) return;
    const newItems = suggestedItems.filter(s => s.sku_id).map(s => ({
      sku_id: s.sku_id, sku_name: s.sku_name || s.item_name, quantity: s.ordered_qty || 1, selling_price: 0, gst_percent: 0, gst_treatment: "Exclusive", mrp: 0, max_discount_sku: 0, effective_max_discount: 0, discount_percent: 0, discount_exceeded: false, moq: 1, moq_error: false, upmc: 0, carton_error: false, _from_po: true
    }));
    setLineItems(prev => [...prev, ...newItems]);
    setSuggestedItems([]);
    toast.success(`${newItems.length} item(s) pre-filled from PO data`);
  };

  // FOC stock items (free items added alongside order)
  const [focItems, setFocItems] = useState([]);
  const [rtoStockItems, setRtoStockItems] = useState([]);
  const [showRtoStock, setShowRtoStock] = useState(false);
  const [allSKUs, setAllSKUs] = useState([]);

  const addFocItem = async () => {
    if (allSKUs.length === 0) {
      try { const { data } = await api.get("/catalogue/skus"); setAllSKUs(data); } catch (err) { console.error("CreateOrderPage error:", err); }
    }
    setFocItems([...focItems, { sku_id: "", sku_name: "", quantity: 1, mrp: 0, reason: "" }]);
  };

  const updateFocItem = (index, field, value) => {
    const items = [...focItems];
    items[index][field] = value;
    if (field === "sku_id") {
      const sku = allSKUs.find(s => s.sku_id === value);
      if (sku) { items[index].sku_name = sku.sku_name; items[index].mrp = sku.mrp || 0; }
    }
    setFocItems(items);
  };

  const removeFocItem = (index) => setFocItems(focItems.filter((_, i) => i !== index));

  const updateLineItem = async (index, field, value) => {
    const items = [...lineItems];
    items[index][field] = value;
    if (field === "sku_id") {
      const sku = channelSKUs.find(s => s.sku_id === value);
      if (sku) {
        const ca = getSKUChannelData(sku);
        const moq = ca?.moq || sku.moq || 1;
        const upmc = parseInt(sku.units_per_master_carton || 0) || 0;
        const cartonOverride = ca?.carton_multiple_override;
        let effectiveMax = 100;
        try {
          const skuMaxDisc = ca?.max_discount_percent;
          const { data: vr } = await api.post("/validate/discount", { discount_percent: 0, sku_channel_max: skuMaxDisc ?? -1, customer_max: order.customer_max_discount ?? -1, channel_max: channels.find(c => c._id === order.channel_id)?.max_discount ?? -1 });
          effectiveMax = vr.effective_max ?? 100;
        } catch (err) { console.error("API post /validate/discount error:", err); }
        // Resolve carton enforce: per-SKU override > channel default
        const channelDoc = channels.find(c => c._id === order.channel_id);
        const channelEnforce = channelDoc?.enforce_carton_multiples !== false;
        const enforceCarton = cartonOverride !== null && cartonOverride !== undefined ? cartonOverride : channelEnforce;
        items[index] = { ...items[index], sku_name: sku.sku_name, selling_price: ca?.selling_price || sku.mrp, mrp: sku.mrp, gst_percent: sku.gst_percent || 0, gst_treatment: sku.gst_treatment || "Exclusive", max_discount_sku: ca?.max_discount_percent ?? -1, effective_max_discount: effectiveMax, discount_percent: 0, discount_exceeded: false, moq, moq_error: false, upmc, enforceCarton, carton_error: false };
      }
    }
    if (field === "quantity") {
      items[index].moq_error = (parseInt(value) || 0) < (items[index].moq || 1);
      const q = parseInt(value) || 0;
      const u = items[index].upmc || 0;
      items[index].carton_error = items[index].enforceCarton && u > 0 && q > 0 && (q % u !== 0);
    }
    if (field === "discount_percent") {
      // Only update local value — validation happens on blur (Fix 2: performance)
      items[index].discount_percent = parseFloat(value) || 0;
    }
    setLineItems(items);
  };

  const validateDiscount = async (index) => {
    const items = [...lineItems];
    const item = items[index];
    if (!item) return;
    try {
      const { data: vr } = await api.post("/validate/discount", { discount_percent: parseFloat(item.discount_percent) || 0, sku_channel_max: item.max_discount_sku ?? -1, customer_max: order.customer_max_discount ?? -1, channel_max: channels.find(c => c._id === order.channel_id)?.max_discount ?? -1 });
      items[index].discount_exceeded = !vr.allowed;
      items[index].effective_max_discount = vr.effective_max;
    } catch { items[index].discount_exceeded = false; }
    setLineItems(items);
  };

  const removeLineItem = (index) => setLineItems(lineItems.filter((_, i) => i !== index));

  // totals and isInterState are derived from server calculation (Rule 11)
  const totals = { netValue: calcResult.totals.net_value || 0, gstAmt: calcResult.totals.gst_amount || 0, cgst: calcResult.totals.cgst || 0, sgst: calcResult.totals.sgst || 0, igst: calcResult.totals.igst || 0, total: calcResult.totals.total || 0 };
  const isInterState = !!calcResult.is_inter_state;

  // ─── PAYMENT HELPERS ────────────────────────────────────────

  const invoiceValue = r2(totals.total);
  const netPayable = r2(Math.max(invoiceValue - totalCNApplied, 0));
  const outstandingBalance = r2(netPayable <= 0 ? 0 : (paymentType === "full_payment" ? 0 : paymentType === "part_advance" ? Math.max(0, netPayable - paymentForm.amount_paid) : netPayable));
  // BATCH 2 (2.10): the Credit Sale box showed a computed date but never put it in state, so
  // "Expected payment date is required" fired and Next would not advance. Set it once, and let
  // the user change it.
  const creditSaleDefaultDate = React.useMemo(() => {
    return istDatePlus(creditCheck?.credit_days || 30);
  }, [creditCheck?.credit_days]);
  React.useEffect(() => {
    if (paymentType === "credit_sale" && !paymentForm.expected_payment_date) {
      setPaymentForm(f => ({ ...f, expected_payment_date: creditSaleDefaultDate }));
      setFieldErrors(f => ({ ...f, expected_payment_date: undefined }));
    }
  }, [paymentType, creditSaleDefaultDate]);  // eslint-disable-line react-hooks/exhaustive-deps

  // FINANCE BATCH 1 (1.4c): a credit sale is "Unpaid" — "credit sale" lives in payment_type.
  // The server derives the stored status from the amounts; this is display only.
  const paymentStatus = paymentType === "full_payment" ? "Fully Paid" : paymentType === "part_advance" ? "Partially Paid" : paymentType === "credit_sale" ? "Unpaid" : "";

  // FX3c Fix 1 — a DP order with an above-cap discount or FOC items walks the same chain as
  // a DP rule breach, so the banner must name them and the reason box must be mandatory.
  const dpExtraExceptions = useMemo(() => {
    const out = [];
    const over = lineItems.filter(i => i.sku_id && i.discount_exceeded);
    if (over.length) {
      out.push({
        code: "discount_above_limit",
        label: "Discount above limit",
        detail: over.map(i => `${i.sku_name || i.sku_id} — limit ${i.effective_max_discount}%, you gave ${i.discount_percent}%`).join("; "),
      });
    }
    const foc = focItems.filter(f => f.sku_id);
    if (foc.length) {
      out.push({
        code: "foc_items",
        label: "Free items (FOC)",
        detail: `${foc.length} free item line(s) on this order`,
      });
    }
    return out;
  }, [lineItems, focItems]);

  const dpAllExceptions = useMemo(
    () => [...((dpInfo && dpInfo.exceptions) || []), ...(dpInfo ? dpExtraExceptions : [])],
    [dpInfo, dpExtraExceptions]);

  useEffect(() => {
    if (!order.customer_id) { setDpInfo(null); return; }
    const paid = netPayable <= 0 ? 0 : (paymentType === "full_payment" ? netPayable : paymentType === "credit_sale" ? 0 : (paymentForm.amount_paid || 0));
    api.get(`/orders/dp-preview/${order.customer_id}`, {
      params: {
        invoice_value: r2(invoiceValue || 0),
        payment_type: netPayable <= 0 ? "credit_sale" : (paymentType || ""),
        amount_paid: r2(paid),
      },
    }).then(r => setDpInfo(r.data?.is_dp ? r.data : null)).catch(() => setDpInfo(null));
  }, [order.customer_id, invoiceValue, paymentType, netPayable, paymentForm.amount_paid]);


  // Fix 7: Block Next on payment step if invalid
  const canProceedFromPayment = () => {
    if (netPayable <= 0) return true;
    if (!paymentType) return false;
    const dateOk = outstandingBalance <= 5 || !!paymentForm.expected_payment_date;
    if (paymentType === "full_payment") return !!(paymentForm.payment_date && paymentForm.payment_mode && paymentForm.payment_reference) && dateOk;
    if (paymentType === "part_advance") return paymentForm.amount_paid > 0 && paymentForm.amount_paid < netPayable && paymentForm.payment_date && paymentForm.payment_mode && paymentForm.payment_reference && dateOk;
    if (paymentType === "credit_sale") return dateOk;
    return false;
  };

  // CN conditional mandatory — removed free-text, using validated list only

  // Step validation
  const validateStep = (s) => {
    const errs = {};
    if (s === 0) {
      if (!order.order_date) errs.order_date = "Order date is required";
      if (!order.po_expiry_not_available && !order.po_expiry_date) errs.po_expiry_date = "PO Expiry Date is required, or check 'No PO expiry date'";
    }
    if (s === 3) {
      if (netPayable <= 0) { /* CN fully covers — no payment validation needed */ }
      else if (paymentType === "full_payment") {
        if (!paymentForm.payment_date) errs.payment_date = "Date of Receipt is required";
        if (!paymentForm.payment_mode) errs.payment_mode = "Payment mode is required";
        if (!paymentForm.payment_reference) errs.payment_reference = "Reference / UTR number is required";
      }
      else if (paymentType === "part_advance") {
        if (!paymentForm.amount_paid || paymentForm.amount_paid <= 0) errs.advance_amount = "Advance amount is required";
        if (!paymentForm.payment_date) errs.payment_date = "Date of receipt is required";
        if (!paymentForm.payment_mode) errs.payment_mode = "Payment mode is required";
        if (!paymentForm.payment_reference) errs.payment_reference = "Reference / UTR number is required";
      }
      // Outstanding-driven expected payment date (applies to ALL payment types)
      if (outstandingBalance > 5 && !paymentForm.expected_payment_date) {
        errs.expected_payment_date = "Expected payment date is required";
      }
    }
    if (s === 4) {
      if (!logistics.dispatch_date) errs.dispatch_date = "Dispatch By date is required";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // FOLLOW-UP 3 (12a) — the payment screenshots attached at punch; the box is cleared whenever the
  // payment type changes, so what is on screen always matches what will be sent
  useEffect(() => { setProofFiles([]); setProofInputKey(k => k + 1); }, [paymentType]);


  const uploadProofFiles = async (files) => {
    const ids = [];
    for (const f of (files || [])) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("entity_type", "order_payment_upload");   // FIX-UP 2 (Y3)
      fd.append("document_type", "payment_proof");
      const { data } = await api.post("/documents/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      ids.push(data._id);
    }
    return ids;
  };

  const Req = () => <span className="text-red-500 ml-0.5">*</span>;
  const FieldErr = ({ name }) => fieldErrors[name] ? <p className="text-[10px] text-red-500 mt-0.5">{fieldErrors[name]}</p> : null;

  const refLabel = paymentForm.payment_mode === "UPI" ? "UTR Number" : paymentForm.payment_mode === "Cheque" ? "Cheque Number" : paymentForm.payment_mode === "Cash" ? "Receipt Number (optional)" : "Reference / UTR Number";

  // ─── DISCOUNT REQUEST ───────────────────────────────────────


  // ─── SUBMIT ORDER ───────────────────────────────────────────

  const handleSubmit = async () => {
    // E2E-2 R-D1/R-D2 — an above-limit discount is allowed; the reason is not optional
    if (lineItems.some(i => i.sku_id && i.discount_exceeded && !(i.discount_reason || "").trim())) { toast.error("Add a reason for the extra discount"); return; }
    if (lineItems.find(i => i.moq_error)) { toast.error("Some items are below minimum order quantity (MOQ). Please fix before submitting."); return; }
    if (lineItems.find(i => i.carton_error)) { toast.error("Some items are not in carton multiples. Please fix before submitting."); return; }
    if (netPayable > 0 && !paymentType) { toast.error("Please select a payment type"); return; }
    // FOLLOW-UP 3 (12b): a UPI payment is only accepted with the customer's screenshot
    if (paymentType !== "credit_sale" && (paymentForm.payment_mode || "").toUpperCase().includes("UPI")
        && proofFiles.length === 0) {
      toast.error("Attach the customer's payment screenshot for a UPI payment.");
      setFieldErrors(f => ({ ...f, payment_proof: "Attach the customer's payment screenshot for a UPI payment." }));
      return;
    }
    if (focItems.some(f => f.sku_id && !f.reason)) { toast.error("Please provide a reason for each FOC stock item"); return; }
    // FX3b item 3, widened by FX3c — the agent's reason is mandatory for ANY exception on a
    // DP order (rule breach, above-cap discount or FOC)
    if (dpInfo && dpAllExceptions.length > 0 && !dpReason.trim()) {
      toast.error("Add a reason for this exception");
      return;
    }
    setSubmitting(true);
    try {
      const actualAmountPaid = netPayable <= 0 ? 0 : (paymentType === "full_payment" ? netPayable : paymentType === "credit_sale" ? 0 : paymentForm.amount_paid);
      const payments = [];
      if (paymentType !== "credit_sale" && actualAmountPaid > 0) {
        // FOLLOW-UP 3 (12a): the files are uploaded FIRST and their ids travel with the payment,
        // so a bad file stops the punch instead of being thrown away as it used to be.
        const proofIds = await uploadProofFiles(proofFiles);
        payments.push({ amount: r2(actualAmountPaid), date: paymentForm.payment_date, mode: paymentForm.payment_mode, reference: paymentForm.payment_reference, notes: paymentForm.payment_notes, proof_documents: proofIds });
      }
      const payload = {
        overdue_acknowledged: softAck,          // FOLLOW-UP 4 (8e/8f)
        channel_id: order.channel_id, channel_name: order.channel_name, customer_id: order.customer_id, customer_name: order.customer_name,
        order_date: order.order_date, order_type: order.order_type, customer_state: order.customer_state,
        po_number: order.po_number || "", po_date: order.po_date || "", pi_reference: order.pi_reference || "",
        po_expiry_date: order.po_expiry_date || "", po_expiry_not_available: order.po_expiry_not_available || false,
        applied_cns: appliedCNs.map(cn => ({ cn_id: cn.cn_id, cn_number: cn.cn_number, amount_applied: cn.amount_applied })),
        total_cn_applied: totalCNApplied,
        credit_override: order.credit_override || false, credit_override_reason: order.credit_override_reason || "",
        dp_exception_reason: dpReason.trim(),
        line_items: lineItems.filter(li => li.sku_id).map((item, idx) => { const c = getCalc(idx); return { sku_id: item.sku_id, sku_name: item.sku_name, quantity: item.quantity, mrp: item.mrp, selling_price: item.selling_price, discount_percent: r2(item.discount_percent || 0), discount_reason: (item.discount_reason || "").trim(), gst_percent: item.gst_percent || 0, line_total: c.net_value, gst_amount: c.gst_amount, net_amount: c.total, is_rto_stock: item.is_rto_stock || false, rto_stock_id: item.rto_stock_id || "", rto_number: item.rto_number || "" }; }),
        discount_type: "auto", flat_discount_percent: 0,
        warehouse_id: order.warehouse_id, warehouse_name: order.warehouse_name,
        fulfilment_type: order.fulfilment_type || "warehouse_dispatch",
        ship_from_location: order.ship_from_location || "", ship_from_transporter: order.ship_from_transporter || "",
        dispatch_date: logistics.dispatch_date, courier_partner: logistics.courier_partner, special_instructions: logistics.special_instructions,
        priority: order.priority || "standard",
        delivery_address: order.delivery_address || null,
        payment_type: netPayable <= 0 ? "credit_sale" : paymentType, payment_status: netPayable <= 0 ? "Fully Paid" : paymentStatus,
        amount_paid: r2(actualAmountPaid),
        payment_mode: paymentForm.payment_mode, payment_reference: paymentForm.payment_reference,
        payment_date: paymentForm.payment_date, expected_payment_date: paymentForm.expected_payment_date,
        payment_notes: paymentForm.payment_notes, payments,
        foc_items: focItems.filter(f => f.sku_id).map(f => ({ sku_id: f.sku_id, sku_name: f.sku_name, quantity: f.quantity, mrp: f.mrp, is_foc: true, reason: f.reason })),
        intended_cn_applications: [],
        order_notes: [
          // Step-level notes from order creation
          ...Object.entries(order.step_notes || {}).filter(([_, note]) => note.trim()).map(([stepName, note]) => ({ section: stepName, note: note.trim(), timestamp: new Date().toISOString(), author: user?.name || "" })),
          ...(focItems.filter(f => f.reason).map(f => ({ section: "FOC Stock", note: `${f.sku_name}: ${f.reason}`, timestamp: new Date().toISOString() }))),
          ...(paymentForm.payment_notes ? [{ section: "Payment Terms", note: paymentForm.payment_notes, timestamp: new Date().toISOString() }] : []),
          ...(logistics.special_instructions ? [{ section: "Logistics", note: logistics.special_instructions, timestamp: new Date().toISOString() }] : []),
          ...(reviewNotes ? [{ section: "Review & Submit", note: reviewNotes, timestamp: new Date().toISOString() }] : []),
        ],
      };
      await api.post("/orders", payload);
      toast.success("Order created successfully");
      navigate("/orders");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "duplicate_po") {
        toast.error(`Duplicate PO: ${detail.message}`, { duration: 8000 });
      } else {
        const msg = formatApiError(detail);
        // FX1 B5 — the brand "do not sell" message also stays on screen in red.
        if (typeof msg === "string" && (msg.includes("cannot be sold") || msg.includes("is not active"))) {
          setBrandBlockMsg(msg);
        }
        toast.error(msg);
      }
    }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="create-order-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/orders")} className="h-8 w-8"><ArrowLeft size={16} /></Button>
        <div><h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">New Order</h1></div>
      </div>

      {/* Steps */}
      <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-2" data-testid="step-indicator">
        {STEPS.map((s, i) => (
          <div key={i} className="flex items-center">
            <button onClick={() => i <= step && setStep(i)} className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-sm ${i === step ? 'bg-zinc-950 text-white' : i < step ? 'bg-zinc-200 text-zinc-700' : 'bg-zinc-100 text-zinc-400'}`} data-testid={`step-${i}`}>
              <span className="w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold border border-current">{i < step ? <Check size={10} /> : i + 1}</span>
              <span className="hidden sm:inline">{s}</span>
            </button>
            {i < STEPS.length - 1 && <div className="w-4 h-px bg-zinc-300 mx-0.5" />}
          </div>
        ))}
      </div>

      <div className="border border-zinc-200 p-6 mb-6">

        {/* ═══ STEP 1: ORDER DETAILS ═══ */}
        {step === 0 && (
          <div data-testid="step-order-details">
            <h3 className="font-heading text-lg font-medium text-zinc-900 mb-4">Order Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Sales Channel</Label>
                <div className="mt-1">
                  <SearchableSelect
                    options={channels.filter(c => c.status === "active").map(ch => ({ value: ch._id, label: ch.name }))}
                    value={order.channel_id}
                    onChange={(v) => onChannelChange(v)}
                    placeholder="Search Sales Channel..."
                    testid="order-channel-select"
                    data-testid="order-channel-select"
                  />
                </div>
              </div>
              <div className="relative">
                <Label className="text-xs">Customer <span className="text-zinc-400">(filtered by {order.channel_name || 'channel'})</span></Label>
                <Input
                  value={order.customer_name || customerSearchText || ""}
                  onChange={(e) => { setCustomerSearchText(e.target.value); setShowCustomerDropdown(true); }}
                  onFocus={() => setShowCustomerDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                  placeholder="Type to search customers..."
                  className="mt-1 rounded-sm" data-testid="order-customer-select"
                />
                {showCustomerDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                    {filteredCustomers
                      .filter(c => !customerSearchText || c.name?.toLowerCase().includes(customerSearchText.toLowerCase()) || c.company_name?.toLowerCase().includes(customerSearchText.toLowerCase()))
                      .map(c => (
                      <button key={c._id} onMouseDown={(e) => { e.preventDefault(); onCustomerChange(c._id); setCustomerSearchText(""); setShowCustomerDropdown(false); }}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-zinc-50 text-sm">
                        <span className="font-medium text-zinc-900">{c.company_name || c.name}</span>
                        {c.company_name && c.name && <span className="text-zinc-500 ml-1">({c.name})</span>}
                        <span className="text-zinc-400 text-xs ml-2">{c.state || ''}</span>
                      </button>
                    ))}
                    {filteredCustomers.filter(c => !customerSearchText || c.name?.toLowerCase().includes(customerSearchText.toLowerCase()) || c.company_name?.toLowerCase().includes(customerSearchText.toLowerCase())).length === 0 && (
                      <p className="px-3 py-3 text-sm text-zinc-400 text-center">No customers found</p>
                    )}
                  </div>
                )}
                {order.customer_id && creditCheck && creditCheck.customer_health && (
                  <div className="mt-1">
                    {creditCheck.customer_health === "good" && !creditCheck.unlock_active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">Good Standing</span>}
                    {creditCheck.customer_health === "due_soon" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Payment Due Soon</span>}
                    {creditCheck.customer_health === "blocked" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-600 text-white font-medium">Blocked — Outstanding Dues</span>}
                    {creditCheck.unlock_active && creditCheck.unlock_info && (
                      <div className="mt-1 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 text-[10px] text-amber-800">
                        Credit block temporarily lifted by <strong>{creditCheck.unlock_info.unlocked_by_name}</strong> until {fmtDate(creditCheck.unlock_info.expires_at)}. Reason: {creditCheck.unlock_info.reason}
                      </div>
                    )}
                  </div>
                )}
                {/* FX3b item 5 — the DP minimum is stated the moment the partner is picked */}
                {dpInfo && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-sm" data-testid="dp-minimum-banner-step1">
                    <p className="text-[11px] font-semibold text-blue-800">
                      Direct Partner order — minimum {fmt(dpInfo.first_order_min)} (first order) / {fmt(dpInfo.repeat_order_min)} (repeat).
                      {" "}This order: <span className="font-mono">{fmt(dpInfo.order_total)}</span>
                    </p>
                    <p className="text-[10px] text-blue-700 mt-0.5">
                      {dpInfo.is_first_order ? "First order for this partner" : `Repeat order (${dpInfo.prior_orders} already placed)`}
                      {" — applicable minimum "}{fmt(dpInfo.applicable_minimum)}
                    </p>
                  </div>
                )}

              </div>
              <div>
                <Label className="text-xs">Order Date <Req /></Label>
                <Input type="date" value={order.order_date} onChange={(e) => { setOrder({...order, order_date: e.target.value}); setFieldErrors(f => ({...f, order_date: undefined})); }} className={`mt-1 rounded-sm ${fieldErrors.order_date ? 'border-red-400' : ''}`} data-testid="order-date-input" />
                <FieldErr name="order_date" />
              </div>
              <div>
                <Label className="text-xs">PO Expiry Date {!order.po_expiry_not_available && <Req />}</Label>
                <Input type="date" value={order.po_expiry_date || ""} onChange={(e) => { setOrder({...order, po_expiry_date: e.target.value}); setFieldErrors(f => ({...f, po_expiry_date: undefined})); }} disabled={order.po_expiry_not_available} className={`mt-1 rounded-sm ${order.po_expiry_not_available ? 'bg-zinc-100 opacity-50' : ''} ${fieldErrors.po_expiry_date ? 'border-red-400' : ''}`} data-testid="po-expiry-date" />
                <label className="flex items-center gap-1.5 mt-1 cursor-pointer"><input type="checkbox" checked={order.po_expiry_not_available || false} onChange={e => setOrder({...order, po_expiry_not_available: e.target.checked, po_expiry_date: e.target.checked ? "" : order.po_expiry_date})} className="w-3 h-3" /><span className="text-[10px] text-zinc-500">No PO expiry date</span></label>
                <FieldErr name="po_expiry_date" />
              </div>
              <div>
                <Label className="text-xs">Order Type</Label>
                <Select value={order.order_type} onValueChange={(v) => setOrder({...order, order_type: v})}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{ORDER_TYPES.map(ot => <SelectItem key={ot} value={ot}>{ot}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Customer PO Number <span className="text-zinc-400">(optional)</span></Label>
                <Input value={order.po_number} onChange={(e) => {
                  const val = e.target.value;
                  setOrder({...order, po_number: val});
                  setPoCheck(null);
                  if (poCheckTimer) clearTimeout(poCheckTimer);
                  if (val.trim()) {
                    setPoCheckTimer(setTimeout(() => {
                      const params = { po_number: val };
                      if (order.customer_id) params.customer_id = order.customer_id;
                      api.get("/orders/check-po", { params }).then(r => setPoCheck(r.data)).catch(err => console.error("API get /orders/check-po error:", err));
                    }, 500));
                  }
                }} className="mt-1 rounded-sm" placeholder="e.g. PO-2026-001" data-testid="order-po-number" />
                {poCheck?.exists && poCheck?.same_customer && (
                  <div className="mt-1 p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-800" data-testid="po-dup-warning">
                    <strong>Duplicate PO</strong> — This PO# already exists on <button onClick={() => navigate(`/orders/${poCheck.existing_order?._id}`)} className="font-mono underline">{poCheck.existing_order?.order_number}</button> for this customer ({poCheck.existing_order?.date}). Please verify before continuing.
                  </div>
                )}
                {poCheck?.exists && !poCheck?.same_customer && (
                  <div className="mt-1 p-2 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-800" data-testid="po-dup-warning-other">
                    This PO# exists on <button onClick={() => navigate(`/orders/${poCheck.existing_order?._id}`)} className="font-mono underline">{poCheck.existing_order?.order_number}</button> for a different customer ({poCheck.existing_order?.customer_name}). Please verify this is correct.
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">PO Date <span className="text-zinc-400">(optional)</span></Label>
                <Input type="date" value={order.po_date} onChange={(e) => setOrder({...order, po_date: e.target.value})} className="mt-1 rounded-sm" data-testid="order-po-date" />
              </div>
              <div>
                <Label className="text-xs">Proforma Invoice Reference <span className="text-zinc-400">(optional)</span></Label>
                <Input value={order.pi_reference} onChange={(e) => setOrder({...order, pi_reference: e.target.value})} className="mt-1 rounded-sm" placeholder="e.g. PI/0001/26-27" data-testid="order-pi-ref" />
              </div>
              <div>
                <Label className="text-xs">Fulfilment</Label>
                <div className="flex gap-2 mt-1">
                  <button type="button" className={`px-3 py-1.5 text-xs rounded-sm border ${order.fulfilment_type !== "external_ship" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300"}`} onClick={() => setOrder({...order, fulfilment_type: "warehouse_dispatch"})}>Our Warehouse</button>
                  <button type="button" className={`px-3 py-1.5 text-xs rounded-sm border ${order.fulfilment_type === "external_ship" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300"}`} onClick={() => setOrder({...order, fulfilment_type: "external_ship", warehouse_id: "", warehouse_name: ""})}>Ship from Another Location</button>
                </div>
              </div>
              {order.fulfilment_type !== "external_ship" ? (
              <div>
                <Label className="text-xs">Warehouse *</Label>
                <Select value={order.warehouse_id || "none"} onValueChange={(v) => { const wh = warehouses.find(w => w._id === (v==="none"?"":v)); setOrder({...order, warehouse_id: v==="none"?"":v, warehouse_name: wh?.name||""}); }}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="order-warehouse"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">-- Select Warehouse --</SelectItem>{warehouses.map(wh => <SelectItem key={wh._id} value={wh._id}>{wh.name}</SelectItem>)}</SelectContent>
                </Select>
                {!order.warehouse_id && <p className="text-[9px] text-red-500 mt-0.5">Warehouse is required</p>}
              </div>
              ) : (
              <div className="space-y-2">
                <div><Label className="text-xs">Ship From Location *</Label><Input value={order.ship_from_location} onChange={e => setOrder({...order, ship_from_location: e.target.value})} className="mt-1 rounded-sm" placeholder="e.g. 3PL Hyderabad / Partner warehouse" data-testid="ship-from-location" /></div>
                <div><Label className="text-xs">Transporter</Label><Input value={order.ship_from_transporter} onChange={e => setOrder({...order, ship_from_transporter: e.target.value})} className="mt-1 rounded-sm" placeholder="Optional" data-testid="ship-from-transporter" /></div>
              </div>
              )}
            </div>

            {/* Order Priority (CR-12) */}
            <div className="mt-4" data-testid="order-priority-section">
              <Label className="text-xs font-semibold">Order Priority</Label>
              <div className="flex gap-4 mt-2">
                {[{v:"standard",l:"Standard",d:"Regular order, normal timeline",c:"border-zinc-200"},{v:"urgent",l:"Urgent",d:"Needs dispatch within 24 hours",c:"border-amber-300 bg-amber-50"},{v:"critical",l:"Critical",d:"Immediate dispatch required",c:"border-red-300 bg-red-50"}].map(p => (
                  <label key={p.v} className={`flex items-start gap-2 px-3 py-2 border rounded-sm cursor-pointer transition-colors ${order.priority === p.v ? p.c : 'border-zinc-200 hover:bg-zinc-50'}`}>
                    <input type="radio" name="priority" value={p.v} checked={order.priority === p.v} onChange={() => setOrder({...order, priority: p.v})} className="mt-0.5 accent-zinc-900" />
                    <div><span className="text-xs font-medium">{p.l}</span><p className="text-[9px] text-zinc-500">{p.d}</p></div>
                  </label>
                ))}
              </div>
            </div>

            {/* DELIVERY ADDRESS SELECTION */}
            {order.customer_id && order._delivery_options && order._delivery_options.length > 0 && (
              <div className="mt-4 border border-zinc-200 rounded-lg p-3" data-testid="delivery-address-section">
                <Label className="text-xs font-semibold text-zinc-700 mb-2 block">Delivery Address</Label>
                {order._delivery_options.length === 1 ? (
                  <div className="bg-zinc-50 rounded-lg p-3 text-sm text-zinc-700">
                    <p className="font-medium">{order._delivery_options[0].label || "Default Address"}</p>
                    <p>{order._delivery_options[0].line1}{order._delivery_options[0].city ? `, ${order._delivery_options[0].city}` : ""}{order._delivery_options[0].state ? `, ${order._delivery_options[0].state}` : ""} {order._delivery_options[0].pincode || ""}</p>
                    {order._delivery_options[0].contact_person && <p className="text-zinc-500 text-xs mt-1">Contact: {order._delivery_options[0].contact_person} {order._delivery_options[0].contact_phone || ""}</p>}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {order._delivery_options.map((da, i) => (
                      <label key={i} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${order.delivery_address_index === i ? 'border-blue-400 bg-blue-50' : 'border-zinc-200 hover:bg-zinc-50'}`}>
                        <input type="radio" name="delivery_addr" checked={order.delivery_address_index === i}
                          onChange={() => setOrder(prev => ({...prev, delivery_address: da, delivery_address_index: i}))}
                          className="mt-1" />
                        <div className="text-sm">
                          <p className="font-medium text-zinc-900">{da.label || `Address ${i + 1}`}</p>
                          <p className="text-zinc-600">{da.line1}{da.city ? `, ${da.city}` : ""}{da.state ? `, ${da.state}` : ""} {da.pincode || ""}</p>
                          {da.contact_person && <p className="text-zinc-500 text-xs mt-0.5">Contact: {da.contact_person} {da.contact_phone || ""}</p>}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* CUSTOMER HISTORY PANEL */}
            {order.customer_id && (
              <div className="mt-4 border border-zinc-200 rounded-sm" data-testid="customer-history-panel">
                <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <History size={14} className="text-zinc-500" />
                    <span className="text-xs font-semibold text-zinc-700">Customer Order History</span>
                  </div>
                  {customerHistory.total > 5 && <span className="text-[10px] text-blue-600 cursor-pointer hover:underline">View all {customerHistory.total} orders</span>}
                </div>
                {customerHistory.orders.length === 0 ? (
                  <p className="px-4 py-4 text-xs text-zinc-400 text-center">No previous orders for this customer.</p>
                ) : (
                  <div className="divide-y divide-zinc-100">
                    {customerHistory.orders.map(ho => (
                      <button key={ho._id} onClick={() => setHistoryOrder(ho)} className="w-full px-4 py-2 flex items-center gap-4 hover:bg-zinc-50 text-left" data-testid={`history-order-${ho._id}`}>
                        <span className="font-mono text-xs font-semibold text-zinc-900 w-20">{ho.order_number}</span>
                        <span className="font-mono text-[10px] text-zinc-500 w-20">{fmtDate(ho.order_date)}</span>
                        <span className="font-mono text-xs text-zinc-900 w-20 text-right">{fmt(ho.invoice_value)}</span>
                        <span className="text-[10px] text-zinc-500 w-12">{ho.line_items?.length || 0} SKUs</span>
                        <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${ORDER_STATUS_COLORS[ho.status] || 'bg-zinc-100 text-zinc-600'}`}>{ho.status}</span>
                        {ho.payment_status && <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${PAY_STATUS_COLORS[ho.payment_status] || ''}`}>{ho.payment_status}</span>}
                        {(ho.outstanding_balance || 0) > 0 && <span className="text-[10px] text-amber-600 font-mono">Due: {fmt(ho.outstanding_balance)}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* History order detail modal */}
            {historyOrder && (
              <Dialog open={!!historyOrder} onOpenChange={() => setHistoryOrder(null)}>
                <DialogContent className="max-w-2xl max-h-[75vh] overflow-y-auto" data-testid="history-order-detail">
                  <DialogHeader><DialogTitle className="font-heading font-mono">{historyOrder.order_number}</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div><span className="text-zinc-400 block text-[10px]">Date</span>{fmtDate(historyOrder.order_date)}</div>
                      <div><span className="text-zinc-400 block text-[10px]">Status</span><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${ORDER_STATUS_COLORS[historyOrder.status] || ''}`}>{historyOrder.status}</span></div>
                      <div><span className="text-zinc-400 block text-[10px]">Invoice Value</span><span className="font-mono font-semibold">{fmt(historyOrder.invoice_value)}</span></div>
                      <div><span className="text-zinc-400 block text-[10px]">Payment</span>{historyOrder.payment_status ? <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${PAY_STATUS_COLORS[historyOrder.payment_status] || ''}`}>{historyOrder.payment_status}</span> : <span className="text-zinc-400">N/A</span>}</div>
                    </div>
                    {(historyOrder.outstanding_balance || 0) > 0 && (
                      <div className="p-2 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-800">Outstanding: {fmt(historyOrder.outstanding_balance)}</div>
                    )}
                    <div className="border border-zinc-200 overflow-x-auto">
                      <table className="data-table w-full">
                        <thead><tr><th>SKU ID</th><th>SKU Name</th><th className="num">Qty</th><th className="num">Disc%</th><th className="num">GST%</th><th className="num">Net Amount</th></tr></thead>
                        <tbody>
                          {(historyOrder.line_items || []).map((li, i) => (
                            <tr key={i}>
                              <td className="font-mono text-xs">{li.sku_id}</td>
                              <td className="text-xs">{li.sku_name}</td>
                              <td className="num font-mono">{li.quantity}</td>
                              <td className="num font-mono">{li.discount_percent || 0}%</td>
                              <td className="num font-mono">{li.gst_percent}%</td>
                              <td className="num font-mono font-semibold">{fmt(li.net_amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {(historyOrder.payments || []).length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold uppercase text-zinc-400 mb-1">Payment History</p>
                        {historyOrder.payments.map((p, i) => (
                          <div key={i} className="flex justify-between text-xs text-zinc-600 border-b border-zinc-50 py-1">
                            <span>{fmtDate(p.date)} — {p.mode}</span>
                            <span className="font-mono font-semibold">{fmt(p.amount)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        )}

        {/* ═══ STEP 2: SKU SELECTION ═══ */}
        {step === 1 && (
          <div data-testid="step-sku-selection">
            {brandBlockMsg && (
              <div className="mb-4 p-3 bg-red-50 border border-red-300 rounded-sm" data-testid="brand-block-error">
                <p className="text-xs font-semibold text-red-700">This order cannot be saved</p>
                <p className="text-xs text-red-700 mt-1">{brandBlockMsg}</p>
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <div><h3 className="font-heading text-lg font-medium text-zinc-900">SKU Selection</h3><p className="text-[10px] text-zinc-500">Only SKUs in <span className="font-semibold">{order.channel_name}</span></p></div>
              <div className="flex gap-2">
                {suggestedItems.length > 0 && <Button onClick={applySuggestedItems} className="rounded-sm h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white" data-testid="apply-suggested">Pre-fill {suggestedItems.filter(s=>s.sku_id).length} items from PO</Button>}
                <Button onClick={addLineItem} variant="outline" className="rounded-sm h-7 text-xs" data-testid="add-line-item"><Plus size={12} /> Add Item</Button>
              </div>
            </div>
            {suggestedItems.length > 0 && (
              <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-sm text-[10px]" data-testid="suggested-items-banner">
                <p className="font-semibold text-blue-700 mb-1">PO Data Available — {suggestedItems.length} item(s) from Blinkit Bulk PO:</p>
                {suggestedItems.map((s, i) => <div key={i} className={s.needs_review ? "text-amber-700" : "text-blue-600"}>{s.needs_review ? "⚠️" : "✓"} {s.item_name} — {s.ordered_qty} units {s.needs_review ? "(SKU not matched — needs review)" : `→ ${s.sku_name}`}</div>)}
              </div>
            )}
            {lineItems.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-zinc-300 rounded-sm"><p className="text-sm text-zinc-400 mb-2">No items added</p><Button onClick={addLineItem} variant="outline" className="rounded-sm text-xs">Add First Item</Button></div>
            ) : lineItems.map((item, i) => {
              const c = getCalc(i);
              const isSampleFOC = order.order_type === "Sample FOC";
              const discountAmt = c.discount_amount || 0;
              return (
                <div key={i} className={`border p-3 rounded-sm mb-3 ${item.discount_exceeded ? 'border-amber-400 bg-amber-50' : 'border-zinc-200'}`} data-testid={`line-item-${i}`}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                    <div className="md:col-span-3">
                      <Label className="text-[10px]">SKU</Label>
                      <SearchableSelect
                        options={channelSKUs.map(s => ({ value: s.sku_id, label: `${s.sku_id} — ${s.sku_name}${s.pack_format ? ` — ${s.pack_format}` : ''}` }))}
                        value={item.sku_id}
                        onChange={(v) => updateLineItem(i, "sku_id", v)}
                        placeholder="Search SKU ID or name..."
                        testid={`line-sku-${i}`}
                        data-testid={`line-sku-${i}`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-[10px]">Qty</Label>
                      <div className="flex items-center mt-1">
                        <button type="button" onClick={() => updateLineItem(i, "quantity", Math.max(1, (item.quantity || 1) - 1))} className="h-10 w-8 shrink-0 flex items-center justify-center border border-zinc-200 border-r-0 rounded-l-sm text-zinc-500 hover:bg-zinc-100 text-sm font-medium">−</button>
                        <Input type="number" min={1} value={item.quantity}
                          onChange={(e) => updateLineItem(i, "quantity", parseInt(e.target.value) || 1)}
                          onFocus={(e) => e.target.select()}
                          className="h-10 text-xs text-center rounded-none border-x-0 flex-1 min-w-0 px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          data-testid={`line-qty-${i}`} />
                        <button type="button" onClick={() => updateLineItem(i, "quantity", (item.quantity || 1) + 1)} className="h-10 w-8 shrink-0 flex items-center justify-center border border-zinc-200 border-l-0 rounded-r-sm text-zinc-500 hover:bg-zinc-100 text-sm font-medium">+</button>
                      </div>
                    </div>
                    <div className="md:col-span-1">
                      <Label className="text-[10px]">Unit Price</Label>
                      <Input type="number" value={isSampleFOC ? 0 : item.selling_price} className="mt-1 rounded-sm h-10 text-xs font-mono text-right bg-zinc-100 text-zinc-500 cursor-default" readOnly tabIndex={-1} />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-[10px]">{isSampleFOC ? "FOC — Zero Value" : <span>Disc% <span className="text-zinc-400 font-normal" data-testid={`line-disc-limit-${i}`}>(Max: {(item.effective_max_discount ?? 100) >= 100 ? "no cap" : `${item.effective_max_discount}%`})</span></span>}</Label>
                      {isSampleFOC ? (
                        <p className="mt-1 h-10 flex items-center text-xs text-green-700 font-semibold">Free of Charge</p>
                      ) : (
                        <>
                          <Input type="text" inputMode="decimal" value={item.discount_percent || ""}
                            onChange={(e) => { const val = e.target.value.replace(/[^0-9.]/g, ""); updateLineItem(i, "discount_percent", parseFloat(val) || 0); }}
                            onFocus={(e) => e.target.select()}
                            onBlur={() => validateDiscount(i)}
                            className={`mt-1 rounded-sm h-10 text-xs text-right ${item.discount_exceeded ? 'border-amber-500 bg-amber-50' : (item.discount_percent > 0 && !item.discount_exceeded) ? 'border-green-400' : ''}`}
                            data-testid={`line-disc-${i}`} placeholder="0" />
                          {item.discount_exceeded && <p className="text-[9px] text-amber-700 mt-0.5" data-testid={`line-over-limit-${i}`}>Limit {item.effective_max_discount}% · You gave {item.discount_percent}% · Needs Admin approval</p>}
                          {discountAmt > 0 && !item.discount_exceeded && (
                            <p className="text-[9px] font-mono text-red-600 text-right mt-0.5">-{fmt(discountAmt)}</p>
                          )}
                        </>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-[10px]">Net Value</Label>
                      <p className="font-mono text-xs font-semibold mt-1 h-10 flex items-center justify-end">{fmt(isSampleFOC ? 0 : c.net_value)}</p>
                    </div>
                    <div className="md:col-span-2 flex items-end gap-1">
                      <div className="flex-1">
                        <Label className="text-[10px]">Total</Label>
                        <p className="font-mono text-xs font-semibold mt-1 h-10 flex items-center justify-end">{fmt(isSampleFOC ? 0 : c.total)}</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeLineItem(i)} className="h-8 w-8 text-red-400"><Trash2 size={14} /></Button>
                    </div>
                  </div>
                  {item.sku_id && !isSampleFOC && <div className="flex flex-wrap gap-4 mt-2 text-[10px] border-t border-zinc-100 pt-2"><span className="text-zinc-400">GST: <span className="font-mono">{item.gst_percent}% ({item.gst_treatment})</span></span>{!isInterState ? <><span className="text-zinc-400">CGST: <span className="font-mono">{fmt(c.cgst)}</span></span><span className="text-zinc-400">SGST: <span className="font-mono">{fmt(c.sgst)}</span></span></> : <span className="text-zinc-400">IGST: <span className="font-mono">{fmt(c.igst)}</span></span>}{item.mrp !== item.selling_price && <span className="text-zinc-400">MRP: <span className="font-mono">{fmt(item.mrp)}</span></span>}{item.moq > 1 && <span className="text-zinc-400">MOQ: <span className="font-mono">{item.moq}</span></span>}</div>}
                  {item.moq_error && <div className="mt-1 p-1.5 bg-amber-50 border border-amber-200 rounded-sm"><p className="text-[10px] text-amber-700 flex items-center gap-1"><AlertCircle size={10} />Minimum order quantity is {item.moq} units. You entered {item.quantity}.</p></div>}
                  {item.carton_error && <div className="mt-1 p-1.5 bg-amber-50 border border-amber-200 rounded-sm"><p className="text-[10px] text-amber-700 flex items-center gap-1"><AlertCircle size={10} />Order in multiples of {item.upmc} (1 carton). You entered {item.quantity}. <button className="underline ml-1" onClick={() => { const nearest = item.upmc * Math.round(item.quantity / item.upmc) || item.upmc; updateLineItem(lineItems.indexOf(item), "quantity", nearest); }}>Round to {item.upmc * Math.round(item.quantity / item.upmc) || item.upmc}</button></p></div>}
                  {item.discount_exceeded && !isSampleFOC && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-300 rounded-sm" data-testid={`discount-reason-box-${i}`}>
                      <p className="text-[10px] text-amber-800 flex items-center gap-1 font-semibold"><ShieldAlert size={12} />Limit {item.effective_max_discount}% · You gave {item.discount_percent}% · Needs Admin approval</p>
                      <Label className="text-[10px] text-amber-800 mt-1 block">Why this extra discount? <span className="text-red-500">*</span></Label>
                      <Input value={item.discount_reason || ""} placeholder="e.g. old stock, expiry in 3 months"
                        onChange={(e) => { const items = [...lineItems]; items[i].discount_reason = e.target.value; setLineItems(items); }}
                        className="mt-1 rounded-sm h-9 text-xs bg-white" data-testid={`discount-reason-${i}`} />
                      {!(item.discount_reason || "").trim() && <p className="text-[9px] text-red-600 mt-0.5">Add a reason for the extra discount</p>}
                    </div>
                  )}
                </div>
              );
            })}

            {/* FOC Stock Section */}
            <div className="mt-6 border-t border-zinc-200 pt-4" data-testid="foc-stock-section">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="text-sm font-semibold text-zinc-900">Add FOC Stock</h4>
                  <p className="text-[10px] text-zinc-500">Free of charge items for this customer. Available from all SKUs. Goes through same approval process.</p>
                </div>
                <Button onClick={addFocItem} variant="outline" className="rounded-sm h-7 text-xs" data-testid="add-foc-item"><Plus size={12} /> Add FOC</Button>
              </div>
              {focItems.map((foc, i) => (
                <div key={i} className="border border-dashed border-green-300 bg-green-50 p-3 rounded-sm mb-2" data-testid={`foc-item-${i}`}>
                  <div className="grid grid-cols-12 gap-3 items-end">
                    <div className="col-span-12 md:col-span-4">
                      <Label className="text-[10px] text-green-700">FOC SKU (any SKU)</Label>
                      <SearchableSelect
                        options={(allSKUs.length > 0 ? allSKUs : channelSKUs).map(s => ({ value: s.sku_id, label: `${s.sku_name} (${s.sku_id})` }))}
                        value={foc.sku_id || ""}
                        onChange={(v) => updateFocItem(i, "sku_id", v)}
                        placeholder="Search SKU..."
                        className="mt-1"
                      />
                    </div>
                    <div className="col-span-3 md:col-span-1">
                      <Label className="text-[10px] text-green-700">Qty</Label>
                      <Input type="number" min={1} value={foc.quantity} onChange={(e) => updateFocItem(i, "quantity", parseInt(e.target.value) || 1)} className="mt-1 rounded-sm h-8 text-xs text-right bg-white" />
                    </div>
                    <div className="col-span-3 md:col-span-2">
                      <Label className="text-[10px] text-green-700">MRP Value</Label>
                      <p className="font-mono text-xs text-zinc-500 mt-1 h-8 flex items-center justify-end">{foc.mrp ? fmt(r2(foc.mrp * foc.quantity)) : "—"}</p>
                    </div>
                    <div className="col-span-3 md:col-span-1">
                      <Label className="text-[10px] text-green-700">Price</Label>
                      <p className="font-mono text-xs font-semibold text-green-700 mt-1 h-8 flex items-center">FREE</p>
                    </div>
                    <div className="col-span-3 md:col-span-2 flex items-end">
                      <Button variant="ghost" size="icon" onClick={() => removeFocItem(i)} className="h-8 w-8 text-red-400"><Trash2 size={14} /></Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    <div>
                      <Label className="text-[10px] text-green-700">Reason for FOC (mandatory)</Label>
                      <Input value={foc.reason} onChange={(e) => updateFocItem(i, "reason", e.target.value)} placeholder="Why is this FOC being given?" className="mt-1 rounded-sm text-xs bg-white" data-testid={`foc-reason-${i}`} />
                      {foc.sku_id && !foc.reason && <p className="text-[10px] text-red-500 mt-0.5">Reason is required</p>}
                    </div>
                    <div>
                      <Label className="text-[10px] text-green-700">Attachment (optional)</Label>
                      <Input type="file" accept="image/*,.pdf" className="mt-1 rounded-sm text-xs bg-white" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Available RTO Stock section — shown in Step 2 if RTO stock exists */}
        {step === 1 && rtoStockItems.length > 0 && (
          <div className="mt-4 border border-amber-200 rounded-sm overflow-hidden" data-testid="rto-stock-section">
            <button onClick={() => setShowRtoStock(!showRtoStock)} className="w-full flex items-center justify-between px-4 py-2 bg-amber-50 text-left hover:bg-amber-100">
              <span className="text-xs font-semibold text-amber-800">Available RTO Stock <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-amber-200 text-amber-900 rounded-sm">{rtoStockItems.length}</span></span>
              <ChevronDown size={14} className={`text-amber-600 transition-transform ${showRtoStock ? 'rotate-180' : ''}`} />
            </button>
            {showRtoStock && (
              <div className="p-3 space-y-2">
                {rtoStockItems.map((rs, idx) => {
                  const daysToExpiry = rs.days_to_expiry;
                  const expiryColor = daysToExpiry === null ? "text-zinc-400" : daysToExpiry > 60 ? "text-green-600" : daysToExpiry >= 30 ? "text-amber-600" : "text-red-600 font-bold";
                  return (
                    <div key={rs._id} className="flex items-center justify-between py-2 px-3 border border-amber-100 rounded-sm bg-white" data-testid={`rto-stock-item-${idx}`}>
                      <div className="flex-1">
                        <p className="text-xs font-medium">{rs.sku_name} <span className="px-1 py-0.5 text-[8px] font-bold bg-amber-100 text-amber-700 rounded-sm ml-1">RTO</span></p>
                        <p className="text-[10px] text-zinc-500">{rs.quantity_boxes} boxes available · Expiry: <span className={expiryColor}>{rs.expiry_date ? fmtDate(rs.expiry_date) : '—'}{daysToExpiry !== null ? ` (${daysToExpiry}d)` : ''}</span> · {rs.rto_number}</p>
                      </div>
                      <div className="text-right mr-3">
                        <p className="text-xs font-mono">{rs.discounted_price !== null ? `₹${rs.discounted_price}` : 'Standard Price'}</p>
                      </div>
                      <Button variant="outline" className="rounded-sm h-7 text-[10px] border-amber-300 text-amber-700" onClick={() => {
                        const sku = channelSKUs.find(s => s.sku_id === rs.sku_id);
                        const price = rs.discounted_price || (sku ? (sku.channel_access || []).find(ca => ca.channel_id === order.channel_id)?.selling_price || sku.mrp : 0);
                        setLineItems(prev => [...prev, {
                          sku_id: rs.sku_id, sku_name: rs.sku_name, quantity: Math.min(rs.quantity_boxes, rs.quantity_boxes),
                          selling_price: price, mrp: sku?.mrp || price, discount_percent: 0, gst_percent: sku?.gst_percent || 18,
                          gst_treatment: sku?.gst_treatment || "Exclusive", effective_max_discount: 100,
                          is_rto_stock: true, rto_stock_id: rs._id, rto_number: rs.rto_number,
                        }]);
                        toast.success(`Added RTO stock: ${rs.sku_name}`);
                      }} data-testid={`add-rto-stock-${idx}`}>Add to Order</Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ═══ STEP 3: PRICING SUMMARY ═══ */}
        {step === 2 && (
          <div data-testid="step-pricing-summary">
            <h3 className="font-heading text-lg font-medium text-zinc-900 mb-4">Pricing Summary</h3>

            {/* FOC-only order banner */}
            {!lineItems.some(i => i.sku_id) && focItems.some(f => f.sku_id) && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
                <strong>FOC-Only Order (₹0 invoice)</strong> — This order contains only free-of-cost items and will require Admin approval.
              </div>
            )}

            {/* Exception detection banner */}
            {(() => {
              const hasFocItems = focItems.some(f => f.sku_id);
              const hasHighDisc = lineItems.some(i => i.discount_exceeded);
              if (hasFocItems || hasHighDisc) {
                return (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2" data-testid="exception-banner">
                    <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">This order contains exceptions and will require Admin approval before Finance</p>
                      <ul className="text-xs text-amber-700 mt-1 list-disc ml-4">
                        {hasFocItems && <li>FOC (Free of Charge) items added</li>}
                        {hasHighDisc && <li>Discount above limit on one or more SKUs</li>}
                      </ul>
                      {hasHighDisc && (
                        <div className="mt-2 bg-white border border-amber-200 rounded-sm p-2" data-testid="discount-above-limit-banner">
                          <p className="text-xs font-semibold text-amber-800 mb-1">Discount above limit — needs Admin approval</p>
                          <table className="w-full text-[10px]">
                            <thead><tr className="text-amber-700"><th className="text-left">SKU</th><th className="text-right">Limit %</th><th className="text-right">Given %</th><th className="text-right">Extra ₹</th><th className="text-left pl-2">Reason</th></tr></thead>
                            <tbody>
                              {lineItems.filter(i2 => i2.sku_id && i2.discount_exceeded).map((i2, k) => (
                                <tr key={k} data-testid={`above-limit-row-${k}`}>
                                  <td className="text-left">{i2.sku_name || i2.sku_id}</td>
                                  <td className="text-right font-mono">{i2.effective_max_discount}%</td>
                                  <td className="text-right font-mono">{i2.discount_percent}%</td>
                                  <td className="text-right font-mono">{fmt(((i2.discount_percent - i2.effective_max_discount) / 100) * (i2.selling_price || i2.mrp || 0) * (i2.quantity || 0))}</td>
                                  <td className="text-left pl-2">{i2.discount_reason || "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      <p className="text-xs text-amber-600 mt-1">Approval flow: Manager → Admin → Finance</p>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            <div className="border border-zinc-200 mb-4 overflow-x-auto">
              <table className="data-table w-full"><thead><tr><th>SKU ID</th><th>Name</th><th className="num">Qty</th><th className="num">Unit Price</th><th className="num">Disc%</th><th className="num">Disc Amt</th><th className="num">Net Value</th><th className="num">GST%</th><th className="num">GST Amt</th>{!isInterState ? <><th className="num">CGST</th><th className="num">SGST</th></> : <th className="num">IGST</th>}<th className="num">Total</th></tr></thead>
              <tbody>{lineItems.map((item, i) => { const c = getCalc(i); const discAmt = c.discount_amount || 0; return (<tr key={i}><td className="font-mono text-xs">{item.sku_id}</td><td className="text-xs">{item.sku_name}</td><td className="num font-mono">{item.quantity}</td><td className="num font-mono">{fmt(item.selling_price || item.mrp)}</td><td className="num font-mono">{item.discount_percent || 0}%</td><td className="num font-mono text-red-600">{discAmt > 0 ? `-${fmt(discAmt)}` : "0.00"}</td><td className="num font-mono">{fmt(c.net_value)}</td><td className="num font-mono">{item.gst_percent}%</td><td className="num font-mono">{fmt(c.gst_amount)}</td>{!isInterState ? <><td className="num font-mono">{fmt(c.cgst)}</td><td className="num font-mono">{fmt(c.sgst)}</td></> : <td className="num font-mono">{fmt(c.igst)}</td>}<td className="num font-mono font-semibold">{fmt(c.total)}</td></tr>); })}</tbody></table>
            </div>

            {/* FOC items in Pricing Summary */}
            {focItems.filter(f => f.sku_id).length > 0 && (
              <div className="mb-4 border border-dashed border-green-300 bg-green-50 rounded-lg overflow-hidden">
                <div className="px-4 py-2 border-b border-green-200 text-xs font-semibold text-green-800">FOC Stock (Free of Charge)</div>
                <table className="data-table w-full">
                  <thead><tr><th>SKU</th><th>Name</th><th className="num">Qty</th><th className="num">MRP Value</th><th className="num">Price</th><th>Reason</th></tr></thead>
                  <tbody>
                    {focItems.filter(f => f.sku_id).map((foc, i) => (
                      <tr key={i} className="bg-green-50">
                        <td className="font-mono text-xs text-green-800">{foc.sku_id}</td>
                        <td className="text-xs text-green-800">{foc.sku_name}</td>
                        <td className="num font-mono">{foc.quantity}</td>
                        <td className="num font-mono text-zinc-500">{foc.mrp ? fmt(foc.mrp * foc.quantity) : "-"}</td>
                        <td className="num font-mono font-semibold text-green-700">FREE</td>
                        <td className="text-xs text-green-700 italic">{foc.reason || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="border border-zinc-200 divide-y divide-zinc-200 max-w-md ml-auto">
              <div className="flex justify-between px-4 py-2"><span className="text-xs text-zinc-500">Net Value</span><span className="font-mono text-xs">{fmt(totals.netValue)}</span></div>
              {!isInterState ? <><div className="flex justify-between px-4 py-2"><span className="text-xs text-zinc-500">CGST</span><span className="font-mono text-xs">{fmt(totals.cgst)}</span></div><div className="flex justify-between px-4 py-2"><span className="text-xs text-zinc-500">SGST</span><span className="font-mono text-xs">{fmt(totals.sgst)}</span></div></> : <div className="flex justify-between px-4 py-2"><span className="text-xs text-zinc-500">IGST</span><span className="font-mono text-xs">{fmt(totals.igst)}</span></div>}
              <div className="flex justify-between px-4 py-3 bg-zinc-50"><span className="text-sm font-semibold">Invoice Value</span><span className="font-mono text-sm font-bold">{fmt(totals.total)}</span></div>
            </div>
          </div>
        )}

        {/* ═══ STEP 4: PAYMENT TERMS ═══ */}
        {step === 3 && (
          <div data-testid="step-payment-terms">
            <h3 className="font-heading text-lg font-medium text-zinc-900 mb-1">Payment Terms</h3>
            <p className="text-[10px] text-zinc-500 mb-4">Invoice value: <span className="font-mono font-semibold">{fmt(invoiceValue)}</span>. Select how this order will be paid.</p>

            {/* CN Available Banner */}
            {availableCNs && availableCNs.cn_count > 0 && appliedCNs.length === 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 mb-4 flex items-center justify-between" data-testid="cn-available-banner">
                <p className="text-xs text-blue-800">This customer has <strong>{availableCNs.cn_count}</strong> available Credit Note(s) totalling <strong>{fmt(availableCNs.total_available)}</strong>.</p>
                <Button size="sm" variant="outline" className="text-xs border-blue-300 text-blue-700" onClick={() => {/* Toggle CN selection */}}>Apply CN</Button>
              </div>
            )}

            {/* CN Applied Summary */}
            {appliedCNs.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 mb-4" data-testid="cn-applied-summary">
                <p className="text-xs font-semibold text-emerald-800 mb-1">Credit Notes Applied</p>
                {appliedCNs.map((cn, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-0.5">
                    <span className="font-mono">{cn.cn_number}</span>
                    <span className="font-mono text-emerald-700">-{fmt(cn.amount_applied)}</span>
                    <Button variant="ghost" size="sm" className="h-5 text-[9px] text-red-500" onClick={() => setAppliedCNs(prev => prev.filter((_, j) => j !== i))}>Remove</Button>
                  </div>
                ))}
                <div className="border-t border-emerald-200 mt-1 pt-1 flex justify-between text-xs font-semibold">
                  <span>Total CN Applied:</span><span className="font-mono text-emerald-700">-{fmt(totalCNApplied)}</span>
                </div>
                <div className="flex justify-between text-xs font-semibold mt-1">
                  <span>Net Payable:</span><span className="font-mono">{fmt(Math.max(invoiceValue - totalCNApplied, 0))}</span>
                </div>
              </div>
            )}

            {/* CN Selection (available CNs list) */}
            {availableCNs && availableCNs.cn_count > 0 && (
              <div className="border border-zinc-200 rounded-sm p-3 mb-4 max-h-64 overflow-y-auto">
                <p className="text-[10px] font-semibold text-zinc-500 mb-2">Available Credit Notes</p>
                {(availableCNs.credit_notes || []).map(cn => {
                  const alreadyApplied = appliedCNs.find(a => a.cn_id === cn._id);
                  const maxApply = Math.min(cn.balance, invoiceValue - totalCNApplied);
                  const canUse = cn.can_use !== false && cn.balance > 0;
                  return (
                    <div key={cn._id} className={`py-1.5 border-b border-zinc-100 ${!canUse && !alreadyApplied ? 'opacity-50' : ''}`}>
                      <div className="flex items-center justify-between text-xs gap-2">
                        <span className="font-mono w-28 shrink-0">{cn.cn_number}</span>
                        <span className="w-20 shrink-0">{fmtDate(cn.cn_date)}</span>
                        <span className="text-zinc-500 truncate flex-1">{cn.reason || "—"}</span>
                        <span className="font-mono w-20 text-right shrink-0">{fmt(cn.amount)}</span>
                        <span className="font-mono w-20 text-right text-emerald-700 shrink-0">Bal: {fmt(cn.balance)}</span>
                        {!canUse && !alreadyApplied ? (
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${cn.status === "Pending Approval" ? "bg-orange-100 text-orange-700" : cn.status === "Rejected" ? "bg-red-100 text-red-700" : "bg-zinc-100 text-zinc-500"}`}>{cn.status}</span>
                        ) : alreadyApplied ? <span className="text-emerald-600 font-medium">Applied {fmt(alreadyApplied.amount_applied)}</span> : (
                          <div className="flex gap-1">
                            <Button variant="outline" size="sm" className="h-5 text-[9px]" onClick={() => {
                              if (maxApply <= 0) { toast.error("No remaining amount to apply"); return; }
                              setAppliedCNs(prev => [...prev, { cn_id: cn._id, cn_number: cn.cn_number, amount_applied: maxApply }]);
                              setPartialCNId(null);
                            }}>Full</Button>
                            <Button variant="outline" size="sm" className="h-5 text-[9px]" onClick={() => { setPartialCNId(partialCNId === cn._id ? null : cn._id); setPartialAmt(""); }}>Partial</Button>
                          </div>
                        )}
                      </div>
                      {partialCNId === cn._id && !alreadyApplied && canUse && (
                        <div className="flex items-center gap-2 mt-1.5 pl-2">
                          <Input type="number" step="0.01" min="0.01" max={maxApply} value={partialAmt} onChange={e => setPartialAmt(e.target.value)} onKeyDown={e => { if (e.key === ',') e.preventDefault(); }} placeholder={`Max ${fmt(maxApply)}`} className="h-6 w-32 text-xs font-mono" />
                          <Button size="sm" className="h-6 text-[9px] px-2" onClick={() => {
                            const val = parseFloat(partialAmt);
                            if (!val || val <= 0) { toast.error("Enter a valid amount"); return; }
                            if (val > maxApply) { toast.error(`Cannot exceed ${fmt(maxApply)}`); return; }
                            setAppliedCNs(prev => [...prev, { cn_id: cn._id, cn_number: cn.cn_number, amount_applied: val }]);
                            setPartialCNId(null); setPartialAmt("");
                          }}>Apply</Button>
                          <Button variant="ghost" size="sm" className="h-6 text-[9px]" onClick={() => setPartialCNId(null)}>Cancel</Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex gap-3 mb-6">
              {[{val:"full_payment",label:"Full Payment Received",icon:CreditCard},{val:"part_advance",label:"Part Advance Payment",icon:CreditCard},{val:"credit_sale",label:"Credit Sale (No Advance)",icon:CreditCard}].map(opt => (
                <button key={opt.val} onClick={() => {
                  if (availableCNs && availableCNs.cn_count > 0 && appliedCNs.length === 0) { setShowCNWarning(opt.val); return; }
                  setPaymentType(opt.val); if (opt.val === "full_payment") setPaymentForm(p => ({...p, amount_paid: invoiceValue})); else if (opt.val === "part_advance") setPaymentForm(p => ({...p, amount_paid: 0}));
                }}
                  className={`flex-1 p-3 border rounded-sm text-left transition-colors ${paymentType === opt.val ? 'border-zinc-950 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'}`} data-testid={`pay-${opt.val}`}>
                  <p className="text-xs font-semibold text-zinc-900">{opt.label}</p>
                </button>
              ))}
            </div>

            {/* CN Warning Dialog */}
            {showCNWarning && (
              <Dialog open={!!showCNWarning} onOpenChange={() => setShowCNWarning(null)}>
                <DialogContent className="max-w-sm" data-testid="cn-warning-dialog">
                  <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><CreditCard size={16} className="text-blue-600" />Credit Notes Available</DialogTitle></DialogHeader>
                  <p className="text-xs text-zinc-700">This customer has <strong>{availableCNs?.cn_count || 0}</strong> available Credit Note(s) totalling <strong>₹{fmt(availableCNs?.total_available || 0)}</strong>. Would you like to apply them before proceeding?</p>
                  <DialogFooter className="gap-2">
                    <Button variant="outline" className="text-xs rounded-sm" data-testid="cn-warning-skip" onClick={() => {
                      setPaymentType(showCNWarning);
                      if (showCNWarning === "full_payment") setPaymentForm(p => ({...p, amount_paid: invoiceValue}));
                      else if (showCNWarning === "part_advance") setPaymentForm(p => ({...p, amount_paid: 0}));
                      setShowCNWarning(null);
                    }}>Skip — Continue Without CN</Button>
                    <Button className="text-xs rounded-sm" data-testid="cn-warning-apply" onClick={() => setShowCNWarning(null)}>Apply CN</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}

            {/* OPTION A: Full Payment */}
            {paymentType === "full_payment" && (
              <div className="space-y-3 border border-zinc-200 p-4 rounded-sm" data-testid="payment-full">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Amount Received (INR)</Label><Input type="number" value={netPayable} className="mt-1 rounded-sm bg-zinc-50 font-mono" readOnly data-testid="pay-amount" /><p className="text-[10px] text-zinc-400 mt-0.5">{totalCNApplied > 0 ? `Invoice ${fmt(invoiceValue)} − CN ${fmt(totalCNApplied)} = Net Payable` : "Auto-filled with invoice value"}. Non-editable.</p></div>
                  <div><Label className="text-xs">Date of Receipt <Req /></Label><Input type="date" value={paymentForm.payment_date} onChange={(e) => { setPaymentForm({...paymentForm, payment_date: e.target.value}); setFieldErrors(f => ({...f, payment_date: undefined})); }} className={`mt-1 rounded-sm ${fieldErrors.payment_date ? 'border-red-400' : ''}`} /><FieldErr name="payment_date" /></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Payment Mode <Req /></Label>
                    <Select value={paymentForm.payment_mode || "none"} onValueChange={(v) => { setPaymentForm({...paymentForm, payment_mode: v === "none" ? "" : v}); setFieldErrors(f => ({...f, payment_mode: undefined})); }}><SelectTrigger className={`mt-1 rounded-sm ${fieldErrors.payment_mode ? 'border-red-400' : ''}`} data-testid="pay-mode"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="none">--</SelectItem>{PAYMENT_MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
                    <FieldErr name="payment_mode" />
                  </div>
                  <div><Label className="text-xs">{refLabel} <Req /></Label><Input value={paymentForm.payment_reference} onChange={(e) => { setPaymentForm({...paymentForm, payment_reference: e.target.value}); setFieldErrors(f => ({...f, payment_reference: undefined})); }} className={`mt-1 rounded-sm ${fieldErrors.payment_reference ? 'border-red-400' : ''}`} data-testid="pay-ref" /><FieldErr name="payment_reference" /></div>
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1"><Upload size={12} /> Payment Proof (screenshot, photo, bank statement){(paymentForm.payment_mode || "").toUpperCase().includes("UPI") ? <Req /> : null}</Label>
                  <Input key={proofInputKey} type="file" accept="image/*,.pdf" multiple onChange={(e) => { setProofFiles(Array.from(e.target.files || [])); setFieldErrors(f => ({ ...f, payment_proof: undefined })); }} className="mt-1 rounded-sm text-xs" data-testid="pay-proof-upload" />
                  <p className="text-[10px] text-zinc-400 mt-0.5">JPG, PNG, PDF accepted, up to 10 MB each. {proofFiles.length > 0
                      ? `${proofFiles.length} file(s) attached.`
                      : (paymentForm.payment_mode || "").toUpperCase().includes("UPI")
                        ? "Required for a UPI payment."
                        : 'Flagged as "Proof Missing" if not uploaded.'}</p>
                  <FieldErr name="payment_proof" />
                </div>
                <div className="p-3 bg-green-50 border border-green-200 rounded-sm"><p className="text-xs font-semibold text-green-800">Outstanding Balance: {fmt(0)} — Fully Paid</p></div>
                <div><Label className="text-xs">Notes</Label><Input value={paymentForm.payment_notes} onChange={(e) => setPaymentForm({...paymentForm, payment_notes: e.target.value})} placeholder="Any additional payment notes" className="mt-1 rounded-sm" /></div>
              </div>
            )}

            {/* OPTION B: Part Advance */}
            {paymentType === "part_advance" && (
              <div className="space-y-3 border border-zinc-200 p-4 rounded-sm" data-testid="payment-advance">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Advance Amount (INR) <Req /></Label><Input type="number" step="0.01" value={paymentForm.amount_paid || ""} onChange={(e) => { setPaymentForm({...paymentForm, amount_paid: parseFloat(e.target.value) || 0}); setFieldErrors(f => ({...f, advance_amount: undefined})); }} className={`mt-1 rounded-sm ${fieldErrors.advance_amount ? 'border-red-400' : ''}`} data-testid="pay-amount" /><FieldErr name="advance_amount" /></div>
                  <div><Label className="text-xs">Date of Receipt <Req /></Label><Input type="date" value={paymentForm.payment_date} onChange={(e) => { setPaymentForm({...paymentForm, payment_date: e.target.value}); setFieldErrors(f => ({...f, payment_date: undefined})); }} className={`mt-1 rounded-sm ${fieldErrors.payment_date ? 'border-red-400' : ''}`} /><FieldErr name="payment_date" /></div>
                </div>
                {paymentForm.amount_paid >= netPayable && paymentForm.amount_paid > 0 && <p className="text-[10px] text-red-600 flex items-center gap-1"><AlertCircle size={10} /> For full payment, select "Full Payment Received" instead.</p>}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Payment Mode <Req /></Label>
                    <Select value={paymentForm.payment_mode || "none"} onValueChange={(v) => { setPaymentForm({...paymentForm, payment_mode: v === "none" ? "" : v}); setFieldErrors(f => ({...f, payment_mode: undefined})); }}><SelectTrigger className={`mt-1 rounded-sm ${fieldErrors.payment_mode ? 'border-red-400' : ''}`}><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="none">--</SelectItem>{PAYMENT_MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
                    <FieldErr name="payment_mode" />
                  </div>
                  <div><Label className="text-xs">{refLabel} <Req /></Label><Input value={paymentForm.payment_reference} onChange={(e) => { setPaymentForm({...paymentForm, payment_reference: e.target.value}); setFieldErrors(f => ({...f, payment_reference: undefined})); }} className={`mt-1 rounded-sm ${fieldErrors.payment_reference ? 'border-red-400' : ''}`} /><FieldErr name="payment_reference" /></div>
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1"><Upload size={12} /> Payment Proof{(paymentForm.payment_mode || "").toUpperCase().includes("UPI") ? <Req /> : null}</Label>
                  <Input key={proofInputKey} type="file" accept="image/*,.pdf" multiple onChange={(e) => { setProofFiles(Array.from(e.target.files || [])); setFieldErrors(f => ({ ...f, payment_proof: undefined })); }} className="mt-1 rounded-sm text-xs" data-testid="pay-proof-upload" />
                  <p className="text-[10px] text-zinc-400 mt-0.5">{proofFiles.length > 0 ? `${proofFiles.length} file(s) attached.` : "JPG, PNG, PDF accepted, up to 10 MB each."}</p>
                  <FieldErr name="payment_proof" />
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm"><p className="text-xs font-semibold text-amber-800">Outstanding Balance: {fmt(Math.max(0, r2(netPayable - paymentForm.amount_paid)))}</p></div>
                <div><Label className="text-xs">When do you expect the balance payment of {fmt(Math.max(0, r2(netPayable - paymentForm.amount_paid)))}? <Req /></Label><Input type="date" value={paymentForm.expected_payment_date} min={istDatePlus(1)} onChange={(e) => { setPaymentForm({...paymentForm, expected_payment_date: e.target.value}); setFieldErrors(f => ({...f, expected_payment_date: undefined})); }} className={`mt-1 rounded-sm ${fieldErrors.expected_payment_date ? 'border-red-400' : ''}`} data-testid="pay-expected-date" /><FieldErr name="expected_payment_date" /></div>
                <div><Label className="text-xs">Notes</Label><Input value={paymentForm.payment_notes} onChange={(e) => setPaymentForm({...paymentForm, payment_notes: e.target.value})} placeholder="e.g. Customer will pay on delivery" className="mt-1 rounded-sm" /></div>
              </div>
            )}

            {/* OPTION C: Credit Sale */}
            {paymentType === "credit_sale" && (
              <div className="space-y-3 border border-zinc-200 p-4 rounded-sm" data-testid="payment-credit">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm"><p className="text-xs font-semibold text-blue-800">Full Amount on Credit: {fmt(invoiceValue)}</p></div>
                {creditCheck && creditCheck.exceeded && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-sm" data-testid="credit-limit-warning">
                    <p className="text-xs font-semibold text-red-800 flex items-center gap-1"><AlertCircle size={12} />Credit Limit Exceeded</p>
                    <p className="text-[10px] text-red-700 mt-1">This will take {creditCheck.customer_name}'s outstanding to {fmt(creditCheck.projected_outstanding)}, exceeding limit of {fmt(creditCheck.credit_limit)}. Order will be flagged for review.</p>
                  </div>
                )}
                <div>
                  <Label className="text-xs">When is the full payment expected? <Req /></Label>
                  <Input type="date" value={paymentForm.expected_payment_date || creditSaleDefaultDate} min={istDatePlus(1)} onChange={(e) => { setPaymentForm({...paymentForm, expected_payment_date: e.target.value}); setFieldErrors(f => ({...f, expected_payment_date: undefined})); }} className={`mt-1 rounded-sm ${fieldErrors.expected_payment_date ? 'border-red-400' : ''}`} data-testid="pay-expected-date" />
                  <FieldErr name="expected_payment_date" />
                  {creditCheck?.credit_days > 0 && <p className="text-[10px] text-zinc-400 mt-1">Suggested: {creditCheck.credit_days} days from today</p>}
                </div>
                <div><Label className="text-xs">Credit Notes</Label><Input value={paymentForm.payment_notes} onChange={(e) => setPaymentForm({...paymentForm, payment_notes: e.target.value})} className="mt-1 rounded-sm" /></div>
              </div>
            )}
          </div>
        )}

        {/* ═══ STEP 5: LOGISTICS ═══ */}
        {step === 4 && (
          <div data-testid="step-logistics">
            <h3 className="font-heading text-lg font-medium text-zinc-900 mb-4">Logistics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label className="text-xs">Warehouse</Label>
                <Input value={order.warehouse_name || "Not selected"} readOnly className="mt-1 rounded-sm bg-zinc-50 text-zinc-500" />
                <p className="text-[9px] text-zinc-400 mt-0.5">Set in Step 1. Go back to change.</p>
              </div>
              <div><Label className="text-xs">Dispatch By <Req /></Label><Input type="date" value={logistics.dispatch_date} min={serverDate} onChange={(e) => { setLogistics({...logistics, dispatch_date: e.target.value}); setFieldErrors(f => ({...f, dispatch_date: undefined})); }} className={`mt-1 rounded-sm ${fieldErrors.dispatch_date ? 'border-red-400' : ''}`} /><FieldErr name="dispatch_date" /></div>
              <div><Label className="text-xs">Courier</Label><Input value={logistics.courier_partner} onChange={(e) => setLogistics({...logistics, courier_partner: e.target.value})} className="mt-1 rounded-sm" /></div>
            </div>
            <div className="mt-4"><Label className="text-xs">Special Instructions</Label><Textarea value={logistics.special_instructions} onChange={(e) => setLogistics({...logistics, special_instructions: e.target.value})} className="mt-1 rounded-sm" rows={2} /></div>
          </div>
        )}

        {/* ═══ STEP 6: REVIEW ═══ */}
        {step === 5 && (
          <div data-testid="step-review">
            <h3 className="font-heading text-lg font-medium text-zinc-900 mb-4">Review &amp; Submit</h3>
            {brandBlockMsg && (
              <div className="mb-4 p-3 bg-red-50 border border-red-300 rounded-sm" data-testid="brand-block-error">
                <p className="text-xs font-semibold text-red-700">This order cannot be saved</p>
                <p className="text-xs text-red-700 mt-1">{brandBlockMsg}</p>
              </div>
            )}

            {/* ═══ FX3b item 5 — Direct Partner order rules ═══ */}
            {dpInfo && (
              <div className="mb-4 space-y-2" data-testid="dp-order-rules-block">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm" data-testid="dp-minimum-banner">
                  <p className="text-xs font-semibold text-blue-800">
                    Direct Partner order — minimum {fmt(dpInfo.first_order_min)} (first order) / {fmt(dpInfo.repeat_order_min)} (repeat).
                    {" "}This order: <span className="font-mono" data-testid="dp-live-total">{fmt(dpInfo.order_total)}</span>
                  </p>
                  <p className="text-[11px] text-blue-700 mt-1">
                    {dpInfo.is_first_order ? "First order for this partner" : `Repeat order (${dpInfo.prior_orders} already placed)`}
                    {" — applicable minimum "}<span className="font-mono">{fmt(dpInfo.applicable_minimum)}</span>
                  </p>
                </div>

                {dpAllExceptions.length > 0 && invoiceValue > 0 && (
                  <div className="p-3 bg-orange-50 border border-orange-300 rounded-sm" data-testid="dp-exception-note">
                    <p className="text-xs font-semibold text-orange-800">
                      This order breaks the Direct Partner rules — it will need approval
                    </p>
                    {dpAllExceptions.map((e, i) => (
                      <p key={i} className="text-[11px] text-orange-800 mt-1" data-testid={`dp-exception-${e.code}`}>
                        <span className="font-semibold">{e.label}:</span> {e.detail}
                      </p>
                    ))}
                    <div className="mt-3">
                      <Label className="text-xs text-orange-900">Reason for this exception <span className="text-red-600">*</span></Label>
                      <Textarea
                        value={dpReason}
                        onChange={(e) => setDpReason(e.target.value)}
                        rows={2}
                        placeholder="Why is this order going out below the rules?"
                        className="mt-1 rounded-sm bg-white"
                        data-testid="dp-exception-reason-input"
                      />
                      {!dpReason.trim() && (
                        <p className="text-[11px] text-red-600 mt-1" data-testid="dp-reason-required-hint">
                          Add a reason for this exception before you submit
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <div><p className="text-[10px] font-semibold uppercase text-zinc-400">Channel</p><p className="text-xs">{order.channel_name}</p></div>
              <div><p className="text-[10px] font-semibold uppercase text-zinc-400">Customer</p><p className="text-xs">{order.customer_name}</p></div>
              <div><p className="text-[10px] font-semibold uppercase text-zinc-400">Date</p><p className="text-xs font-mono">{fmtDate(order.order_date)}</p></div>
              <div><p className="text-[10px] font-semibold uppercase text-zinc-400">Type</p><p className="text-xs">{order.order_type}</p></div>
              <div><p className="text-[10px] font-semibold uppercase text-zinc-400">Payment</p>{paymentStatus && <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${PAY_STATUS_COLORS[paymentStatus] || ''}`}>{paymentStatus}</span>}</div>
            </div>

            {/* Line items with discount */}
            <div className="border border-zinc-200 mb-4 overflow-x-auto">
              <table className="data-table w-full"><thead><tr><th>SKU</th><th className="num">Qty</th><th className="num">Unit Price</th><th className="num">Disc%</th><th className="num">Disc Amt</th><th className="num">Net</th><th className="num">GST</th><th className="num">Total</th></tr></thead>
              <tbody>{lineItems.map((item, i) => { const c = getCalc(i); const dAmt = c.discount_amount || 0; return (<tr key={i}><td className="text-xs">{item.sku_name}</td><td className="num font-mono">{item.quantity}</td><td className="num font-mono">{fmt(item.selling_price || item.mrp)}</td><td className="num font-mono">{item.discount_percent || 0}%</td><td className="num font-mono text-red-600">{dAmt > 0 ? `-${fmt(dAmt)}` : "—"}</td><td className="num font-mono">{fmt(c.net_value)}</td><td className="num font-mono">{fmt(c.gst_amount)}</td><td className="num font-mono font-semibold">{fmt(c.total)}</td></tr>); })}</tbody></table>
            </div>

            {/* FOC items */}
            {focItems.filter(f => f.sku_id).length > 0 && (
              <div className="mb-4">
                <p className="text-[10px] font-semibold uppercase text-zinc-400 mb-1">FOC Stock</p>
                <div className="border border-dashed border-green-300 bg-green-50 rounded-sm divide-y divide-green-200">
                  {focItems.filter(f => f.sku_id).map((foc, i) => (
                    <div key={i} className="px-3 py-2 flex items-center justify-between">
                      <div><span className="text-xs font-medium text-green-800">{foc.sku_name}</span> <span className="text-[10px] text-zinc-500">x{foc.quantity}</span></div>
                      <div className="text-right"><span className="text-[10px] text-zinc-500">MRP: {fmt(r2(foc.mrp * foc.quantity))}</span> <span className="text-xs font-semibold text-green-700 ml-2">FREE</span></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-xs text-zinc-700 space-y-1">
                <p>Warehouse: {logistics.warehouse_name || '-'}</p>
                <p>Dispatch By: {logistics.dispatch_date || '-'}</p>
                <p>Courier: {logistics.courier_partner || '-'}</p>
                {paymentType === "full_payment" && <p className="text-green-700">Full Payment via {paymentForm.payment_mode}</p>}
                {paymentType === "part_advance" && <p className="text-amber-700">Advance: {fmt(paymentForm.amount_paid)} | Balance: {fmt(outstandingBalance)} due {paymentForm.expected_payment_date}</p>}
                {paymentType === "credit_sale" && <p className="text-blue-700">Credit: {fmt(invoiceValue)} due {paymentForm.expected_payment_date}</p>}
              </div>
              <div className="border border-zinc-200 p-3">
                <div className="flex justify-between mb-1"><span className="text-xs text-zinc-500">Net</span><span className="font-mono text-xs">{fmt(totals.netValue)}</span></div>
                <div className="flex justify-between mb-1"><span className="text-xs text-zinc-500">GST</span><span className="font-mono text-xs">{fmt(totals.gstAmt)}</span></div>
                <div className="flex justify-between pt-2 border-t border-zinc-200"><span className="text-sm font-bold">Invoice Total</span><span className="font-mono text-sm font-bold">{fmt(totals.total)}</span></div>
              </div>
            </div>

            {/* Notes / Comments trail */}
            {(focItems.some(f => f.reason) || paymentForm.payment_notes || logistics.special_instructions) && (
              <div className="mb-4 border border-zinc-200 rounded-sm">
                <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200"><p className="text-[10px] font-semibold uppercase text-zinc-400">Notes & Comments</p></div>
                <div className="divide-y divide-zinc-100">
                  {focItems.filter(f => f.reason).map((foc, i) => (
                    <div key={`foc-${i}`} className="px-3 py-2"><span className="text-[10px] font-semibold text-green-700">FOC Stock</span><p className="text-xs text-zinc-700">{foc.sku_name}: {foc.reason}</p></div>
                  ))}
                  {paymentForm.payment_notes && <div className="px-3 py-2"><span className="text-[10px] font-semibold text-blue-700">Payment Terms</span><p className="text-xs text-zinc-700">{paymentForm.payment_notes}</p></div>}
                  {logistics.special_instructions && <div className="px-3 py-2"><span className="text-[10px] font-semibold text-orange-700">Logistics</span><p className="text-xs text-zinc-700">{logistics.special_instructions}</p></div>}
                </div>
              </div>
            )}

            {/* Review comment box */}
            <div className="mb-2">
              <Label className="text-xs">Order Comments / Notes</Label>
              <Textarea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} placeholder="Any final comments for this order..." className="mt-1 rounded-sm" rows={2} data-testid="review-notes" />
            </div>
          </div>
        )}
      </div>

      {/* Step Notes — persistent across all steps */}
      <div className="mb-4 pt-3 border-t border-zinc-100">
        <Label className="text-[10px] text-zinc-400">Note for {STEPS[step]} (optional)</Label>
        <Textarea value={(order.step_notes || {})[STEPS[step]] || ""} onChange={e => setOrder(prev => ({...prev, step_notes: {...(prev.step_notes || {}), [STEPS[step]]: e.target.value}}))} className="mt-1 rounded-sm text-xs" rows={2} placeholder="Add a note for this step..." data-testid="step-note-input" />
      </div>

      {softOverdue.length > 0 && (
        <div className="w-full border border-amber-300 bg-amber-50 rounded-sm p-3 mb-3" data-testid="soft-block-box">
          <p className="text-xs font-semibold text-amber-900 mb-2">⚠️ Payment overdue — recover it to avoid a hard block.</p>
          {softOverdue.map((o, i) => (
            <ul key={i} className="text-[11px] text-amber-900 mb-2 ml-3 list-disc" data-testid={`soft-block-order-${i}`}>
          <li>Order: <span className="font-mono font-semibold">{o.order_number}</span></li>
          <li>Due date: <span className="font-mono">{fmtDate(o.due_date)}</span></li>
          <li>Overdue: {o.days_overdue} day{o.days_overdue === 1 ? "" : "s"}</li>
          <li>Outstanding: <span className="font-mono">₹{fmt(o.outstanding)}</span></li>
          <li>Hard block from: <span className="font-mono font-semibold">{fmtDate(o.hard_block_date)}</span></li>
            </ul>
          ))}
          <label className="flex items-center gap-2 cursor-pointer" data-testid="soft-block-ack-row">
            <input type="checkbox" checked={softAck} onChange={(e) => setSoftAck(e.target.checked)}
               className="rounded" data-testid="soft-block-ack" />
            <span className="text-xs font-semibold text-amber-900">I&apos;ve seen this</span>
          </label>
        </div>
          )}

      {/* Navigation */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-2">
        <Button variant="outline" onClick={() => step > 0 ? setStep(step - 1) : navigate("/orders")} className="rounded-sm text-xs min-h-[44px] md:min-h-0" data-testid="prev-step"><ArrowLeft size={14} /> {step === 0 ? "Cancel" : "Previous"}</Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={() => {
            // Step validation — cannot proceed without required data
            if (step === 0) {
              if (!validateStep(0)) return;
              if (!order.channel_id) { toast.error("Please select a Sales Channel"); return; }
              if (!order.customer_id) { toast.error("Please select a Customer"); return; }
              if (order.fulfilment_type !== "external_ship" && !order.warehouse_id) { toast.error("Please select a warehouse before proceeding"); return; }
              if (order.fulfilment_type === "external_ship" && !order.ship_from_location.trim()) { toast.error("Ship-from location is required for external shipments"); return; }
            }
            if (step === 1) {
              const hasPaidItems = lineItems.some(i => i.sku_id);
              const hasFocItems = focItems.some(f => f.sku_id);
              if (!hasPaidItems && !hasFocItems) { toast.error("Please add at least one SKU or FOC item"); return; }
              if (lineItems.some(i => i.sku_id && i.moq_error)) { toast.error("Some items are below minimum order quantity (MOQ)"); return; }
              if (lineItems.some(i => i.sku_id && i.carton_error)) { toast.error("Some items are not in carton multiples"); return; }
              if (lineItems.some(i => i.sku_id && i.discount_exceeded && !(i.discount_reason || "").trim())) { toast.error("Add a reason for the extra discount"); return; }
            }
            if (step === 3) { if (netPayable > 0 && !validateStep(3)) return; if (netPayable > 0 && !canProceedFromPayment()) { toast.error(paymentType ? "Complete all required payment fields" : "Select a payment type"); return; } }
            if (step === 4 && !validateStep(4)) return;
            setStep(step + 1);
          }} className="rounded-sm text-xs" data-testid="next-step">Next <ArrowRight size={14} /></Button>
        ) : (
          <Button onClick={handleSubmit} disabled={submitting || (softOverdue.length > 0 && !softAck)} className="rounded-sm text-xs" data-testid="submit-order">{submitting ? "Submitting..." : "Submit Order"} <Check size={14} /></Button>        )}
      </div>

      {/* Credit Block Modal */}
      {creditBlock && (
        <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center" data-testid="credit-block-modal">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-y-auto p-6">
            <h2 className="text-lg font-bold text-red-700 mb-3">Order Blocked — Customer Has Outstanding Dues</h2>
            {creditBlock.reasons?.filter(r => r.type === "overdue").map((r, i) => (
              <div key={i} className="mb-3">
                <p className="text-xs font-semibold text-red-600 mb-1">Overdue Invoices ({r.orders.length})</p>
                <table className="w-full text-xs border"><thead><tr className="bg-red-50"><th className="text-left p-1.5">Order#</th><th className="text-right p-1.5">Invoice</th><th className="text-right p-1.5">Outstanding</th><th className="p-1.5">Due Date</th><th className="text-right p-1.5">Days Overdue</th><th className="p-1.5">Hard block from</th></tr></thead>
                <tbody>{r.orders.map((o, j) => <tr key={j} className="border-t"><td className="p-1.5 font-mono">{o.order_number}</td><td className="p-1.5 text-right font-mono">₹{fmt(o.invoice_total)}</td><td className="p-1.5 text-right font-mono text-red-600">₹{fmt(o.outstanding)}</td><td className="p-1.5 font-mono">{fmtDate(o.due_date)}</td><td className="p-1.5 text-right text-red-600 font-semibold">{o.days_overdue}d</td><td className="p-1.5 font-mono">{fmtDate(o.hard_block_date)}</td></tr>)}</tbody></table>
              </div>
            ))}
            {creditBlock.reasons?.filter(r => r.type === "credit_limit").length > 0 && (
              <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                <p className="font-semibold text-amber-700">Credit Limit Exceeded</p>
                <p>Outstanding: ₹{fmt(creditBlock.total_outstanding)} | Limit: ₹{fmt(creditBlock.credit_limit)} | Utilization: {creditBlock.credit_utilization_pct}%</p>
              </div>
            )}
            {creditBlock.reasons?.filter(r => r.type === "unclosed").map((r, i) => (
              <div key={i} className="mb-3">
                <p className="text-xs font-semibold text-blue-600 mb-1">Orders Ready to Close — Fully Paid but Not Closed ({r.orders.length})</p>
                <table className="w-full text-xs border"><thead><tr className="bg-blue-50"><th className="text-left p-1.5">Order#</th><th className="text-right p-1.5">Invoice</th><th className="p-1.5">Status</th></tr></thead>
                <tbody>{r.orders.map((o, j) => <tr key={j} className="border-t"><td className="p-1.5 font-mono">{o.order_number}</td><td className="p-1.5 text-right font-mono">{fmt(o.invoice_total)}</td><td className="p-1.5">{o.status}</td></tr>)}</tbody></table>
              </div>
            ))}
            <p className="text-xs text-zinc-500 mt-3">{isAdmin ? "" : "Contact Admin to temporarily unblock this customer."}</p>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" className="text-xs" onClick={() => navigate(`/orders?customer_id=${order.customer_id}`)}>Go to Customer Orders</Button>
              <Button variant="outline" className="text-xs" onClick={() => { setCreditBlock(null); setOrder(p => ({...p, customer_id: "", customer_name: ""})); setCreditCheck(null); }}>Cancel</Button>
            </div>
            {isAdmin && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-xs font-semibold text-blue-700 mb-2">Temporarily Unblock This Customer</p>
                <div className="flex gap-2 items-end mb-2">
                  <div>
                    <Label className="text-[10px]">Unblock for</Label>
                    <select value={overrideReason.split("|")[0] || "10"} onChange={e => setOverrideReason(e.target.value + "|" + (overrideReason.split("|")[1] || ""))} className="mt-1 h-7 w-28 text-xs border border-zinc-200 rounded px-1">
                      <option value="7">7 days</option><option value="10">10 days</option><option value="15">15 days</option><option value="30">30 days</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <Label className="text-[10px]">Reason for unblock *</Label>
                    <Input value={overrideReason.split("|")[1] || ""} onChange={e => setOverrideReason((overrideReason.split("|")[0] || "10") + "|" + e.target.value)} className="mt-1 h-7 text-xs" placeholder="e.g. Net 30 payment cycle, dues expected by June 15" />
                  </div>
                </div>
                <Button className="text-xs" disabled={(overrideReason.split("|")[1] || "").length < 10} onClick={async () => {
                  const days = parseInt(overrideReason.split("|")[0]) || 10;
                  const reason = (overrideReason.split("|")[1] || "").trim();
                  try {
                    const { data } = await api.post("/orders/credit-block-unlock", { customer_id: order.customer_id, unlock_days: days, reason });
                    toast.success(`Customer unblocked for ${days} days (until ${fmtDate(data.expires_at)}). All users can now create orders.`);
                    setCreditBlock(null);
    setSoftOverdue([]); setSoftAck(false); setOverrideReason("10|");
                    // Refresh credit check
                    const { data: cc } = await api.get(`/customers/${order.customer_id}/credit-check`);
                    setCreditCheck(cc);
                  } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                }}>Unblock Customer</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


export default function CreateOrderPage() {
  return <CreateOrderErrorBoundary><CreateOrderPageInner /></CreateOrderErrorBoundary>;
}
